import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StatCard } from "@/components/StatCard";
import {
  Scissors,
  Users,
  User,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  CreditCard,
  UserPlus,
  UserMinus,
  RefreshCw,
  Search,
  Download,
  BarChart3,
  BarChart,
  PieChart,
  Activity,
  Shield,
  Settings,
  LogOut,
  Bell,
  Eye,
  EyeOff,
  Mail,
  Crown,
  Sparkles,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  Trash2,
  Edit,
  ShieldCheck,
  ShieldAlert,
  UserCog,
  Home,
  ArrowLeft,
  Presentation,
  Share2,
  Copy,
  MessageSquare,
  Send,
  FileText,
  Megaphone,
  UserCheck,
  Building2,
  Gift,
  Bug,
  Lightbulb,
  Heart,
  Trophy,
  Palette,
  BadgeCheck,
  BookOpen,
  HelpCircle,
  Briefcase,
  Code,
  Zap,
  Rocket,
  Globe,
  Plus,
  Star
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import ClientInviteTools from "@/components/ClientInviteTools";
import VerificationDashboard from "@/components/VerificationDashboard";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AIChatbot from "@/components/AIChatbot";
import AdminSoldierPortal from "@/components/AdminSoldierPortal";
import SoldierPayoutDashboard from "@/components/SoldierPayoutDashboard";
import SuperSoldierPayoutDashboard from "@/components/SuperSoldierPayoutDashboard";
import AdminTeamManagement from "@/components/AdminTeamManagement";
import StripeFeeSettings from "@/components/StripeFeeSettings";
import SuperAdminUserManager from "@/components/SuperAdminUserManager";
import SoldiersSettingsEditor from "@/components/SoldiersSettingsEditor";
import AccountantManagement from "@/components/AccountantManagement";
import ReportsDashboard from "@/components/ReportsDashboard";
import BetaManagement from "@/components/BetaManagement";
import LookFestAdmin from "@/components/LookFestAdmin";
import SoldierRecruitmentPlaybook from "@/components/SoldierRecruitmentPlaybook";
import SoldierAgreementManager from "@/components/SoldierAgreementManager";
import SoldierRecruitmentCards from "@/components/SoldierRecruitmentCards";
import EmailCampaignTab from "@/components/EmailCampaignTab";

const SUPER_ADMIN_EMAIL = "georgebroughton71@gmail.com";

const AdminDashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterTier, setFilterTier] = useState("all");
  const [dateRange, setDateRange] = useState("30d");
  
  // Token for API calls
  const token = localStorage.getItem("token");
  
  // Admin management state
  const [admins, setAdmins] = useState([]);
  const [adminPermissions, setAdminPermissions] = useState(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showEditAdmin, setShowEditAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ email: "", name: "", admin_role: "moderator" });
  const [switchingRole, setSwitchingRole] = useState(false);

  // Promotions management state
  const [adminPromotions, setAdminPromotions] = useState([]);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [newPromo, setNewPromo] = useState({
    name: "",
    description: "",
    promotion_type: "free_premium",
    tier: "pro",
    duration_days: 7,
    max_redemptions: null,
    promo_code: "",
    end_date: ""
  });

  // HairChair Soldiers state
  const [soldierApplications, setSoldierApplications] = useState([]);
  const [soldierStats, setSoldierStats] = useState(null);
  const [soldierFilter, setSoldierFilter] = useState("pending");
  const [soldierLoading, setSoldierLoading] = useState(false);

  // User management state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    tier: "pro",
    complimentary: true,
    duration_months: 1
  });

  // Messaging state
  const [messageRecipients, setMessageRecipients] = useState("all");
  const [messageSubject, setMessageSubject] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Subscriber management state
  const [subscriptionSearch, setSubscriptionSearch] = useState("");
  const [viewingSubscriber, setViewingSubscriber] = useState(null);
  const [showSubscriberModal, setShowSubscriberModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [subscriberMessage, setSubscriberMessage] = useState({ subject: "", body: "" });
  const [accessLogs, setAccessLogs] = useState([]);
  
  // Stat card drill-down state
  const [showStatCardModal, setShowStatCardModal] = useState(false);
  const [statCardUsers, setStatCardUsers] = useState([]);
  const [statCardTitle, setStatCardTitle] = useState("");
  
  // Site Analytics state
  const [siteAnalytics, setSiteAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Pricing settings state
  const [pricingSettings, setPricingSettings] = useState({
    pro_price: 19.99,
    elite_price: 39.99,
    pro_trial_days: 7,
    elite_trial_days: 7
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  // Message templates
  const messageTemplates = [
    {
      id: "beta_welcome",
      name: "🎉 Beta Welcome",
      icon: Gift,
      color: "from-amber-500 to-yellow-500",
      subject: "Welcome to MyHairChair Beta - Your Premium Account is Ready!",
      content: `Hi there! 👋

Welcome to the MyHairChair Beta Program! 🎉

You have been given a PREMIUM ACCOUNT for 30 days to help us create the world's best Salon/Barber Management System.

We need your honest feedback - tell us:
✅ What's working well
❌ What's not working  
💡 What needs improvement

Here's how you can share your thoughts:
🐛 Report a Bug - Use the bug report feature
💡 Suggest a Feature - We're all ears!
💬 Chat with us - Use our chatbot
📧 Email directly - info@myhairchair.com

🔥 DON'T MISS: Check out our MyHairChair Soldiers Affiliate Program!
High-performing Salon Owners and Stylists/Barbers can earn REAL CASH FOR LIFE by helping us spread the word. Only 50 spots available - they're filling up FAST!

We have a lot of work to do, so let us know where to begin. It's OK to be honest because we want to get it right!

Let's build something amazing together! 💪

George Broughton III
Founder & CEO, MyHairChair`
    },
    {
      id: "new_feature",
      name: "✨ New Feature Announcement",
      icon: Sparkles,
      color: "from-purple-500 to-pink-500",
      subject: "New Feature Alert! Check Out What's New on MyHairChair",
      content: `Hey there! 👋

We've got exciting news - a brand new feature just dropped on MyHairChair!

🆕 [FEATURE NAME]
[Describe the feature and its benefits]

How to try it:
1. Log into your dashboard
2. [Steps to access the feature]
3. Let us know what you think!

Your feedback makes MyHairChair better for everyone. Drop us a line at info@myhairchair.com

Keep styling! ✂️
The MyHairChair Team`
    },
    {
      id: "crm_marketing_tools",
      name: "📱 CRM Marketing Tools",
      icon: Palette,
      color: "from-pink-500 to-orange-500",
      subject: "🚀 NEW: Digital Marketing Toolkit is LIVE! Share Your Brand Like a Pro",
      content: `Hey there, Boss! 👋

Big news dropping today - January 5th, 2026! 🎉

We just launched the Digital Marketing Toolkit in your Marketing CRM, and it's about to change how you promote your business!

📱 WHAT'S NEW: Professional Social Cards

We've created 5 stunning, ready-to-share social media cards designed specifically for our industry:

✂️ Hair Stylist Card - "Book Your Next Session"
💅 Beautician Card - "Your Beauty Session Awaits" 
💈 Barber Card - "Your Barber is on MyHairChair!"
🎖️ Soldier Recruitment Card - "Join the Founding 50"
👔 CEO Message Card - A personal message from our founder

🔥 WHY THIS IS A GAME-CHANGER:

• Professional images included - No more boring text posts!
• Auto-share to SMS, Email, Instagram, Facebook, X (Twitter)
• Your name, shop name & booking link auto-populated
• QR code included for easy scanning
• UTM tracking to see who's clicking
• One tap download - ready for IG Stories!

💡 HOW TO USE IT:

1. Go to your Dashboard → Marketing/CRM → Social Cards
2. Pick your template (Stylist, Beautician, Barber, etc.)
3. Hit "Send via Text/SMS" or any share button
4. The image downloads automatically + opens the app!
5. Share with your clients and watch those bookings roll in! 📈

Remember: In this industry, if you're not on Instagram, you basically don't exist. These cards make you look PRO! 💪

This feature is available NOW for all Pro & Elite members. 

Got questions? Hit up info@myhairchair.com

Let's grow your business together! 🚀

George Broughton III
Founder & CEO, MyHairChair

P.S. - Don't forget to add your Instagram handle in your profile settings. It'll show up on your cards! 📸`
    },
    {
      id: "soldiers_invite",
      name: "🎖️ Soldiers Program Invite",
      icon: Trophy,
      color: "from-amber-500 to-orange-500",
      subject: "You're Invited: Join the MyHairChair Soldiers Elite Affiliate Program",
      content: `Hey Superstar! 🌟

You've been crushing it, and we noticed! We'd love to invite you to join the exclusive MyHairChair Soldiers Affiliate Program.

🎖️ What is MyHairChair Soldiers?
An elite affiliate program for high-performing salon owners and stylists/barbers who want to earn REAL CASH FOR LIFE!

💰 The 10/10/8 Commission Engine:
• 10% on stylist subscriptions you refer
• 10% on their first 3 months
• 8% recurring for LIFE

🔥 Only 50 spots available - and they're going FAST!

Ready to join the ranks? Apply now at: [SOLDIERS LINK]

Questions? Hit us up at info@myhairchair.com

Let's get that bread! 🍞💰

George Broughton III
Founder & CEO, MyHairChair`
    },
    {
      id: "feedback_request",
      name: "💬 Feedback Request",
      icon: MessageSquare,
      color: "from-blue-500 to-cyan-500",
      subject: "Quick Question: How's Your MyHairChair Experience?",
      content: `Hi there! 👋

We hope you're enjoying MyHairChair! We're always looking to improve, and your opinion matters a LOT to us.

Could you take 2 minutes to share your thoughts?

🤔 What do you love about MyHairChair?
🔧 What could be better?
💡 Any features you wish we had?

Ways to share feedback:
📧 Reply to this email
🐛 Use the Bug Report feature
💡 Submit a Feature Request
💬 Chat with our support bot

Every piece of feedback helps us build a better platform for YOU!

Thanks for being part of the MyHairChair family! 🙏

The MyHairChair Team`
    },
    {
      id: "maintenance",
      name: "🔧 Maintenance Notice",
      icon: Settings,
      color: "from-gray-500 to-slate-500",
      subject: "Scheduled Maintenance Notice - MyHairChair",
      content: `Hi there,

We wanted to give you a heads up about some scheduled maintenance.

🗓️ When: [DATE & TIME]
⏱️ Duration: Approximately [X] hours
🔧 What's happening: We're making improvements to [AREA]

During this time, you may experience:
• Brief service interruptions
• Slower loading times

We'll have everything back up and running ASAP!

Questions? Reach out at info@myhairchair.com

Thanks for your patience! 🙏

The MyHairChair Team`
    },
    {
      id: "thank_you",
      name: "❤️ Thank You",
      icon: Heart,
      color: "from-red-500 to-pink-500",
      subject: "Thank You for Being Part of MyHairChair!",
      content: `Hey there! 👋

Just wanted to take a moment to say THANK YOU! 🙏

Your support means the world to us. Whether you're:
✂️ A stylist growing your business
💇 A client discovering amazing talent
🏢 A salon owner managing your team

You're part of what makes MyHairChair special.

We're working hard every day to make this platform better for YOU. Keep the feedback coming - we're listening!

Have an amazing day! ✨

With gratitude,
George Broughton III
Founder & CEO, MyHairChair`
    },
    {
      id: "investor_letter",
      name: "💰 Investor Introduction",
      icon: Briefcase,
      color: "from-emerald-500 to-teal-500",
      subject: "Market Disruption Opportunity: MyHairChair (1.5M Pros / $65B Industry)",
      content: `Dear [INVESTOR NAME],

My name is George Broughton III, Founder and CEO of MyHairChair, a GoNowBaby Media Group LLC brand. I am reaching out because your expertise in fintech and vertical SaaS aligns perfectly with our mission to reclaim the "App Tax" for beauty professionals.


THE FOUNDER ADVANTAGE

Before MyHairChair, I built SterileMate.com from the ground up into a successful B2B medical supply company. With no prior industry experience, I scaled that business to annual 7-figure revenue by securing and managing complex contracts with major healthcare organizations. I am now applying that same aggressive, lean scaling strategy to the $65B beauty services market.


THE PROBLEM: THE "APP TAX" & FRAGMENTATION

The U.S. market has over 1.1 million beauty professionals currently using legacy platforms like Square, Vagaro, and StyleSeat. However, these pros are frustrated:

• High Transaction Fees: Legacy apps force users into closed payment loops, costing a solo barber $3,000+ annually in fees.

• Payout Delays: Most platforms hold funds for 2–3 days.

• Medical Gap: None of the "Big Four" provide clinical-grade scalp and hair-health tracking, a rising demand in the "Health-Aware" beauty movement.


OUR DISRUPTIVE SOLUTION

MyHairChair is an all-in-one platform under the GoNowBaby Media Group LLC umbrella (EIN: 41-3329219). We serve the entire ecosystem:

• Payment Agnosticism: We allow pros to keep 100% of their revenue via Zelle, CashApp, and PayPal integration, or use Stripe for traditional processing.

• Specialized Health Logs: Medical-grade intake forms and scalp-care tracking (leveraging my healthcare background).

• Booth Management: Automated rent collection and "Landlord-Tenant" logic for salon owners.

• Viral Growth: Our "HairChair Soldiers" program uses a 10/10/8 recurring commission structure to turn users into our sales force.


TRACTION & 3-YEAR PROJECTIONS

We are currently in BETA with our first cohort. Our model targets a 10–15% market capture of the 1.5M existing digital users within 36 months:

• Year 1: 5,000 stylists | $1.5M ARR

• Year 2: 25,000 stylists | $7.5M ARR

• Year 3: 75,000 stylists | $18M ARR (Targeting 77%+ Net Margins)


THE ASK

I'd love 20 minutes to show you the platform and discuss how MyHairChair fits your investment thesis. We are raising a seed round to accelerate user acquisition and complete our mobile infrastructure.

👉 View Investor Profile: https://myhairchair.com/investors

I appreciate your time and look forward to connecting.


Best regards,

George Broughton III
Founder & CEO, MyHairChair
GoNowBaby Media Group LLC
info@myhairchair.com`,
      // HTML version for email clients (preserves formatting when pasted)
      htmlContent: `<p>Dear [INVESTOR NAME],</p>

<p>My name is George Broughton III, Founder and CEO of MyHairChair, a GoNowBaby Media Group LLC brand. I am reaching out because your expertise in fintech and vertical SaaS aligns perfectly with our mission to reclaim the "App Tax" for beauty professionals.</p>

<p><strong>THE FOUNDER ADVANTAGE</strong></p>

<p>Before MyHairChair, I built SterileMate.com from the ground up into a successful B2B medical supply company. With no prior industry experience, I scaled that business to annual 7-figure revenue by securing and managing complex contracts with major healthcare organizations. I am now applying that same aggressive, lean scaling strategy to the $65B beauty services market.</p>

<p><strong>THE PROBLEM: THE "APP TAX" &amp; FRAGMENTATION</strong></p>

<p>The U.S. market has over 1.1 million beauty professionals currently using legacy platforms like Square, Vagaro, and StyleSeat. However, these pros are frustrated:</p>

<ul>
<li><strong>High Transaction Fees:</strong> Legacy apps force users into closed payment loops, costing a solo barber $3,000+ annually in fees.</li>
<li><strong>Payout Delays:</strong> Most platforms hold funds for 2–3 days.</li>
<li><strong>Medical Gap:</strong> None of the "Big Four" provide clinical-grade scalp and hair-health tracking, a rising demand in the "Health-Aware" beauty movement.</li>
</ul>

<p><strong>OUR DISRUPTIVE SOLUTION</strong></p>

<p>MyHairChair is an all-in-one platform under the GoNowBaby Media Group LLC umbrella (EIN: 41-3329219). We serve the entire ecosystem:</p>

<ul>
<li><strong>Payment Agnosticism:</strong> We allow pros to keep 100% of their revenue via Zelle, CashApp, and PayPal integration, or use Stripe for traditional processing.</li>
<li><strong>Specialized Health Logs:</strong> Medical-grade intake forms and scalp-care tracking (leveraging my healthcare background).</li>
<li><strong>Booth Management:</strong> Automated rent collection and "Landlord-Tenant" logic for salon owners.</li>
<li><strong>Viral Growth:</strong> Our "HairChair Soldiers" program uses a 10/10/8 recurring commission structure to turn users into our sales force.</li>
</ul>

<p><strong>TRACTION &amp; 3-YEAR PROJECTIONS</strong></p>

<p>We are currently in BETA with our first cohort. Our model targets a 10–15% market capture of the 1.5M existing digital users within 36 months:</p>

<ul>
<li>Year 1: 5,000 stylists | $1.5M ARR</li>
<li>Year 2: 25,000 stylists | $7.5M ARR</li>
<li>Year 3: 75,000 stylists | $18M ARR (Targeting 77%+ Net Margins)</li>
</ul>

<p><strong>THE ASK</strong></p>

<p>I'd love 20 minutes to show you the platform and discuss how MyHairChair fits your investment thesis. We are raising a seed round to accelerate user acquisition and complete our mobile infrastructure.</p>

<p>👉 <a href="https://myhairchair.com/investors">View Investor Profile</a></p>

<p>I appreciate your time and look forward to connecting.</p>

<p>Best regards,</p>

<p><strong>George Broughton III</strong><br/>
Founder &amp; CEO, MyHairChair<br/>
GoNowBaby Media Group LLC<br/>
<a href="mailto:info@myhairchair.com">info@myhairchair.com</a></p>`
    },
    {
      id: "developer_partnership",
      name: "💻 Developer Partnership",
      icon: Code,
      color: "from-violet-500 to-purple-500",
      subject: "Partnership Opportunity: Build the Future of Salon Tech with MyHairChair",
      content: `Dear [DEVELOPER/AGENCY NAME],

I'm George Broughton III, Founder and CEO of MyHairChair. I'm reaching out about a unique partnership opportunity that combines equity ownership with meaningful work in a high-growth market.

THE OPPORTUNITY

MyHairChair is disrupting the $65B+ salon services industry with an all-in-one platform serving salon owners, stylists, barbers, and their clients. We're seeking a technical partner to help us scale.

WHAT WE'VE BUILT

Our beta platform already includes:
• Multi-role user system (owners, stylists, clients)
• Online booking with multiple payment options
• Receipt verification for manual payments (CashApp/Zelle/PayPal)
• Loyalty rewards program with tier progression
• Pro Mini-Sites for stylists
• AI-powered chatbot and video tutorials
• Booth rent management for salon owners

WHAT WE NEED

A development partner to help us:
• Build native iOS and Android apps
• Scale infrastructure for growth
• Implement advanced analytics
• Integrate additional payment processors

THE PARTNERSHIP STRUCTURE

We're offering a hybrid compensation model:

💰 15% Net Profit Share → 15% Common Stock
   (Vesting over 3 years with immediate profit participation)

Based on our projections:
• Year 3 profit share: ~$200K/year
• Equity stake at $72M exit: $10.8M potential value

This isn't just contract work - it's a chance to be a co-builder of something significant.

NEXT STEPS

I'd love to discuss this opportunity in detail and show you the platform.

👉 View our full partnership proposal: https://myhairchair.com/partnership

Looking forward to exploring this together.

Best regards,

George Broughton III
Founder & CEO, MyHairChair
A Gonowbaby Media Group LLC Brand
info@myhairchair.com`,
      // HTML version for email clients
      htmlContent: `<p>Dear [DEVELOPER/AGENCY NAME],</p>

<p>I'm George Broughton III, Founder and CEO of MyHairChair. I'm reaching out about a unique partnership opportunity that combines equity ownership with meaningful work in a high-growth market.</p>

<p><strong>THE OPPORTUNITY</strong></p>

<p>MyHairChair is disrupting the $65B+ salon services industry with an all-in-one platform serving salon owners, stylists, barbers, and their clients. We're seeking a technical partner to help us scale.</p>

<p><strong>WHAT WE'VE BUILT</strong></p>

<p>Our beta platform already includes:</p>
<ul>
<li>Multi-role user system (owners, stylists, clients)</li>
<li>Online booking with multiple payment options</li>
<li>Receipt verification for manual payments (CashApp/Zelle/PayPal)</li>
<li>Loyalty rewards program with tier progression</li>
<li>Pro Mini-Sites for stylists</li>
<li>AI-powered chatbot and video tutorials</li>
<li>Booth rent management for salon owners</li>
</ul>

<p><strong>WHAT WE NEED</strong></p>

<p>A development partner to help us:</p>
<ul>
<li>Build native iOS and Android apps</li>
<li>Scale infrastructure for growth</li>
<li>Implement advanced analytics</li>
<li>Integrate additional payment processors</li>
</ul>

<p><strong>THE PARTNERSHIP STRUCTURE</strong></p>

<p>We're offering a hybrid compensation model:</p>

<p>💰 <strong>15% Net Profit Share → 15% Common Stock</strong><br/>
(Vesting over 3 years with immediate profit participation)</p>

<p>Based on our projections:</p>
<ul>
<li>Year 3 profit share: ~$200K/year</li>
<li>Equity stake at $72M exit: $10.8M potential value</li>
</ul>

<p>This isn't just contract work - it's a chance to be a co-builder of something significant.</p>

<p><strong>NEXT STEPS</strong></p>

<p>I'd love to discuss this opportunity in detail and show you the platform.</p>

<p>👉 <a href="https://myhairchair.com/partnership">View our full partnership proposal</a></p>

<p>Looking forward to exploring this together.</p>

<p>Best regards,</p>

<p><strong>George Broughton III</strong><br/>
Founder &amp; CEO, MyHairChair<br/>
A GoNowBaby Media Group LLC Brand<br/>
<a href="mailto:info@myhairchair.com">info@myhairchair.com</a></p>`
    }
  ];

  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  const isAdmin = isSuperAdmin || admins.some(a => a.email === user?.email && a.active);

  // Role switch handler for Super View demo mode
  const handleSwitchRole = async (newRole, subscriptionTier = null) => {
    setSwitchingRole(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/switch-role`, 
        { role: newRole, subscription_tier: subscriptionTier },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const tierInfo = subscriptionTier ? ` with ${subscriptionTier.toUpperCase()} subscription` : "";
      toast.success(`Super View: Switched to ${newRole}${tierInfo}. Redirecting...`);
      
      // Update local storage with new user data
      if (res.data.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }
      
      // Redirect to appropriate dashboard
      setTimeout(() => {
        if (newRole === "client") {
          window.location.href = "/dashboard";
        } else if (newRole === "stylist" || newRole === "owner") {
          window.location.href = "/stylist";
        }
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to switch role");
      setSwitchingRole(false);
    }
  };

  useEffect(() => {
    checkAdminAccess();
  }, [user, navigate, authLoading]);

  // Re-fetch analytics when date range changes
  useEffect(() => {
    if (adminPermissions?.is_super_admin) {
      fetchSiteAnalytics();
    }
  }, [dateRange]);

  const checkAdminAccess = async () => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate("/login");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Check admin permissions
      const permRes = await axios.get(`${API}/admin/my-permissions`, { headers });
      setAdminPermissions(permRes.data);
      
      if (!permRes.data.is_admin) {
        toast.error("Admin access required");
        navigate("/");
        return;
      }
      
      // Fetch admin list if super admin
      if (permRes.data.is_super_admin) {
        const adminsRes = await axios.get(`${API}/admin/admins`, { headers });
        setAdmins(adminsRes.data.admins || []);
        // Fetch site analytics for super admin
        fetchSiteAnalytics();
        // Fetch pricing settings for super admin
        fetchPricingSettings();
      }
      
      fetchDashboardData();
    } catch (error) {
      console.error("Admin access check failed:", error);
      toast.error("Admin access required");
      navigate("/");
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, usersRes, subsRes, activityRes] = await Promise.all([
        axios.get(`${API}/admin/stats?range=${dateRange}`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/subscriptions`, { headers }),
        axios.get(`${API}/admin/activity`, { headers })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setSubscriptions(subsRes.data.subscriptions || []);
      setRecentActivity(activityRes.data.activity || []);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      // Show real zeros - no mock data
      setStats({
        totalUsers: 0,
        newUsersToday: 0,
        newUsersThisWeek: 0,
        newUsersThisMonth: 0,
        activeSubscriptions: 0,
        freeUsers: 0,
        proUsers: 0,
        eliteUsers: 0,
        monthlyRevenue: 0,
        revenueGrowth: 0,
        churnRate: 0,
        totalBookings: 0,
        bookingsToday: 0,
        cancellations: 0,
        renewals: 0,
        trialConversions: 0
      });
      setUsers([]);
      setSubscriptions([]);
      setRecentActivity([]);
      toast.error("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch site analytics for super admin
  const fetchSiteAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/site-analytics/stats?range=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteAnalytics(res.data);
    } catch (error) {
      console.log("Site analytics not available:", error.response?.status);
      // Only super admin can access - silently fail for others
      setSiteAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch pricing settings
  const fetchPricingSettings = async () => {
    setPricingLoading(true);
    try {
      const res = await axios.get(`${API}/membership/pricing`);
      const tiers = res.data.tiers || {};
      setPricingSettings({
        pro_price: tiers.pro?.price || 19.99,
        elite_price: tiers.elite?.price || 39.99,
        pro_trial_days: tiers.pro?.trial_days || 7,
        elite_trial_days: tiers.elite?.trial_days || 7
      });
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    } finally {
      setPricingLoading(false);
    }
  };

  // Save pricing settings
  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/membership/pricing`, pricingSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pricing updated successfully!");
      fetchPricingSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pricing");
    } finally {
      setSavingPricing(false);
    }
  };

  // Fetch admin promotions
  const fetchAdminPromotions = async () => {
    setPromoLoading(true);
    try {
      const res = await axios.get(`${API}/promotions/admin/list?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminPromotions(res.data.promotions || []);
    } catch (error) {
      console.error("Failed to fetch promotions:", error);
      toast.error("Failed to load promotions");
    } finally {
      setPromoLoading(false);
    }
  };

  // Create new promotion
  const handleCreatePromotion = async () => {
    if (!newPromo.name || !newPromo.duration_days) {
      toast.error("Please fill in promotion name and duration");
      return;
    }
    
    setPromoLoading(true);
    try {
      const promoData = {
        ...newPromo,
        max_redemptions: newPromo.max_redemptions ? parseInt(newPromo.max_redemptions) : null,
        duration_days: parseInt(newPromo.duration_days),
        promo_code: newPromo.promo_code || null,
        end_date: newPromo.end_date || null
      };
      
      const res = await axios.post(`${API}/promotions/admin/create`, promoData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      setShowCreatePromo(false);
      setNewPromo({
        name: "",
        description: "",
        promotion_type: "free_premium",
        tier: "pro",
        duration_days: 7,
        max_redemptions: null,
        promo_code: "",
        end_date: ""
      });
      fetchAdminPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promotion");
    } finally {
      setPromoLoading(false);
    }
  };

  // Toggle promotion active status
  const handleTogglePromotion = async (promotionId) => {
    try {
      await axios.put(`${API}/promotions/admin/${promotionId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promotion status updated");
      fetchAdminPromotions();
    } catch (error) {
      toast.error("Failed to update promotion");
    }
  };

  // Delete promotion
  const handleDeletePromotion = async (promotionId) => {
    if (!confirm("Are you sure you want to delete this promotion?")) return;
    
    try {
      await axios.delete(`${API}/promotions/admin/${promotionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promotion deleted");
      fetchAdminPromotions();
    } catch (error) {
      toast.error("Failed to delete promotion");
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = searchQuery === "" || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === "all" || u.role === filterRole;
    const matchesTier = filterTier === "all" || u.tier === filterTier;
    return matchesSearch && matchesRole && matchesTier;
  });

  // Admin management functions
  const handleAddAdmin = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/admins`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setShowAddAdmin(false);
      setNewAdmin({ email: "", name: "", admin_role: "moderator" });
      checkAdminAccess(); // Refresh admin list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add admin");
    }
  };

  const handleUpdateAdmin = async (email, updates) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/admins/${email}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin updated successfully");
      setShowEditAdmin(null);
      checkAdminAccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update admin");
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!confirm(`Are you sure you want to remove ${email} as an admin?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/admins/${email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin removed successfully");
      checkAdminAccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove admin");
    }
  };

  // HairChair Soldiers Functions
  const fetchSoldierApplications = async (status = null) => {
    setSoldierLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = status && status !== "all" ? `?status=${status}` : "";
      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/soldiers/admin/applications${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/soldiers/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSoldierApplications(appsRes.data.applications || []);
      setSoldierStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch soldier applications:", error);
      toast.error("Failed to load soldier applications");
    } finally {
      setSoldierLoading(false);
    }
  };

  const handleApproveSoldier = async (applicationId) => {
    if (!confirm("Approve this application and send welcome email?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/soldiers/admin/applications/${applicationId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Application approved! Welcome email sent.");
      fetchSoldierApplications(soldierFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve application");
    }
  };

  const handleDenySoldier = async (applicationId) => {
    const reason = prompt("Denial reason (optional):");
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/soldiers/admin/applications/${applicationId}/deny`, 
        { denial_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Application denied");
      fetchSoldierApplications(soldierFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deny application");
    }
  };

  // User Management Functions
  const handleViewUser = async (userId) => {
    setUserLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ensure we have the expected data structure
      const userData = res.data;
      if (!userData.user) {
        throw new Error("Invalid user data received");
      }
      // Set defaults for missing properties
      userData.user.name = userData.user.name || "Unknown";
      userData.user.email = userData.user.email || "";
      userData.user.role = userData.user.role || "client";
      userData.user.membership = userData.user.membership || { tier: "free" };
      userData.stats = userData.stats || { total_bookings: 0, completed_bookings: 0, referrals_made: 0 };
      
      setSelectedUser(userData);
      setShowUserDetails(true);
    } catch (error) {
      console.error("Error loading user:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to load user details");
    } finally {
      setUserLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User updated successfully");
      setShowEditUserModal(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API}/admin/users/${selectedUser.user.user_id}/subscription`,
        subscriptionForm,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(res.data.message);
      setShowSubscriptionModal(false);
      fetchDashboardData();
      // Refresh selected user
      handleViewUser(selectedUser.user.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subscription");
    }
  };

  const handleCancelSubscription = async (userId) => {
    if (!confirm("Are you sure you want to cancel this user's subscription?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription cancelled");
      fetchDashboardData();
      if (selectedUser) handleViewUser(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    }
  };

  const handleSuspendUser = async (userId) => {
    const reason = prompt("Enter suspension reason:");
    if (!reason) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/suspend`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User suspended");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suspend user");
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User reactivated");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    }
  };

  // Make user a Soldier
  const handleMakeSoldier = async (userId, email, name) => {
    if (!confirm(`Make ${name} (${email}) a Soldier?\n\nThis will:\n• Grant soldier status\n• Create a referral code\n• Send them to Soldier Academy`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/make-soldier`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${name} is now a Soldier! They'll receive academy instructions.`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to make soldier");
    }
  };

  // Promote Soldier to Super Soldier
  const handleMakeSuperSoldier = async (userId, email, name) => {
    if (!confirm(`Promote ${name} (${email}) to SUPER SOLDIER?\n\nThis grants:\n• Platoon management\n• Override commissions\n• Soldier approval rights\n• Advanced analytics`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/make-super-soldier`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${name} is now a Super Soldier! 🎖️`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote to super soldier");
    }
  };

  // Subscriber Management Handlers
  const handleViewSubscriber = async (subscriber) => {
    try {
      const token = localStorage.getItem("token");
      
      // Log access
      await axios.post(`${API}/admin/access-log`, {
        user_id: subscriber.user_id || subscriber.id,
        action: "viewed_subscriber",
        details: `Viewed subscription details for ${subscriber.email || subscriber.user}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch full user details
      const res = await axios.get(`${API}/admin/users/${subscriber.user_id || subscriber.id}/full`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get access logs for this user
      const logsRes = await axios.get(`${API}/admin/access-log/${subscriber.user_id || subscriber.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setViewingSubscriber({ ...subscriber, ...res.data });
      setAccessLogs(logsRes.data.logs || []);
      setShowSubscriberModal(true);
    } catch (error) {
      // If full details fail, just show what we have
      setViewingSubscriber(subscriber);
      setShowSubscriberModal(true);
    }
  };

  const handleMessageSubscriber = (subscriber) => {
    setViewingSubscriber(subscriber);
    setSubscriberMessage({ subject: "", body: "" });
    setShowMessageModal(true);
  };

  const handleEditSubscription = (subscriber) => {
    setViewingSubscriber(subscriber);
    setSubscriptionForm({
      tier: subscriber.tier || "pro",
      complimentary: false,
      duration_months: 1
    });
    setShowSubscriptionModal(true);
  };

  const handleSendSubscriberMessage = async () => {
    if (!subscriberMessage.subject || !subscriberMessage.body) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    setSendingMessage(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${viewingSubscriber.user_id || viewingSubscriber.id}/message`, {
        subject: subscriberMessage.subject,
        body: subscriberMessage.body
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Message sent successfully");
      setShowMessageModal(false);
      setSubscriberMessage({ subject: "", body: "" });
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateSubscriptionFromModal = async (userId, tier, action) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${userId}/subscription`, {
        tier,
        action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Subscription ${action}d successfully`);
      fetchDashboardData();
      setShowSubscriberModal(false);
    } catch (error) {
      toast.error(`Failed to ${action} subscription`);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!confirm(`Are you sure you want to DELETE ${email}? This action cannot be undone.`)) return;
    if (!confirm("This will permanently remove the user and all their data. Continue?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted");
      setShowUserDetails(false);
      setSelectedUser(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleTestNotifications = async () => {
    const phone = prompt("Enter phone number to test SMS:");
    const email = prompt("Enter email to test:");
    if (!phone || !email) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/send-test-notifications`, 
        { phone, email },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.data.sms?.success) {
        toast.success("SMS sent successfully!");
      } else {
        toast.error(`SMS failed: ${res.data.sms?.error || "Unknown error"}`);
      }
      
      if (res.data.email?.success) {
        toast.success("Email sent successfully!");
      } else {
        toast.error(`Email failed: ${res.data.email?.error || "Unknown error"}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notifications");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "super_admin": return "bg-red-500 text-white";
      case "admin": return "bg-purple-500 text-white";
      case "moderator": return "bg-blue-500 text-white";
      case "analyst": return "bg-green-500 text-white";
      default: return "bg-gray-500 text-white";
    }
  };

  // Clickable stat card that opens user list
  const handleStatCardClick = async (cardType) => {
    try {
      const token = localStorage.getItem("token");
      let filter = {};
      let title = "";
      
      switch (cardType) {
        case "total_users":
          filter = { role: "all" };
          title = "All Users";
          break;
        case "free_users":
          filter = { membership: "free" };
          title = "Free Users";
          break;
        case "pro_subscribers":
          filter = { membership: "pro" };
          title = "Pro Subscribers";
          break;
        case "elite_subscribers":
          filter = { membership: "elite" };
          title = "Elite Subscribers";
          break;
        case "active_subscriptions":
          filter = { membership: "pro,elite" };
          title = "Active Subscriptions";
          break;
        case "new_today":
          filter = { created_today: true };
          title = "New Users Today";
          break;
        case "renewals":
          filter = { renewed_recently: true };
          title = "Recent Renewals";
          break;
        case "trial_to_paid":
          filter = { converted: true };
          title = "Trial to Paid Conversions";
          break;
        default:
          return;
      }
      
      // Fetch users based on filter
      const res = await axios.get(`${API}/admin/users/by-filter`, {
        params: filter,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStatCardUsers(res.data.users || []);
      setStatCardTitle(title);
      setShowStatCardModal(true);
    } catch (error) {
      console.error("Failed to load users:", error);
      // Fallback - just show the lookup modal
      setStatCardTitle(cardType.replace(/_/g, " "));
      setShowStatCardModal(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-stone-950/80 backdrop-blur-xl border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Home/Back Button */}
            <Link 
              to="/" 
              className="flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors group"
              data-testid="admin-home-btn"
            >
              <Home className="w-4 h-4 text-gray-400 group-hover:text-white" />
              <span className="text-sm text-gray-400 group-hover:text-white hidden sm:inline">Home</span>
            </Link>
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-white">Super Admin Dashboard</h1>
              <p className="text-xs text-red-400">MyHairChair Platform Control Center</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* User Guide Button */}
            <Link 
              to="/user-guide?role=owner"
              className="p-2 rounded-lg bg-stone-800 hover:bg-stone-700 transition-colors"
              title="User Guide"
              data-testid="user-guide-header-link"
            >
              <BookOpen className="w-5 h-5 text-stone-400 hover:text-white" />
            </Link>
            {/* Demo Package Button */}
            <Link 
              to="/demo-package"
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
              data-testid="demo-package-btn"
            >
              <Presentation className="w-4 h-4" />
              <span className="hidden sm:inline">Demo Package</span>
            </Link>
            {/* Recruitment Playbook - Super Admin has soldier access */}
            <Button
              onClick={() => setShowPlaybook(true)}
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
              data-testid="admin-playbook-btn"
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Playbook</span>
            </Button>
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
              <Bell className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
              <Settings className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 px-3 py-1 bg-stone-800 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-sm">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="text-left">
                <span className="text-sm text-gray-300 block">{user?.name || user?.email}</span>
                <span className="text-xs text-red-400">Super Admin</span>
              </div>
            </div>
            <Button variant="ghost" onClick={logout} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Role Switcher Card - Super Admin Only */}
        {isSuperAdmin && (
          <Card className="bg-gradient-to-br from-red-500/10 to-purple-500/10 border-red-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    <UserCog className="w-5 h-5 text-red-400" />
                    🎬 Super View (Demo Mode)
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Your current role: <Badge className="ml-1 bg-amber-500 text-black">{user?.role || "client"}</Badge>
                    {user?.subscription_tier && user?.subscription_tier !== "free" && (
                      <Badge className={`ml-1 ${user?.subscription_tier === "elite" ? "bg-purple-500" : "bg-amber-500 text-black"}`}>
                        {user?.subscription_tier?.toUpperCase()}
                      </Badge>
                    )}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Switch your view to demo the platform as different user types with subscriptions
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleSwitchRole("client")}
                    disabled={switchingRole || user?.role === "client"}
                    className={`${user?.role === "client" 
                      ? "bg-green-500/20 text-green-400 border border-green-500/50" 
                      : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Client
                    {user?.role === "client" && <CheckCircle className="w-4 h-4 ml-2" />}
                  </Button>
                  <Button
                    onClick={() => handleSwitchRole("stylist", "pro")}
                    disabled={switchingRole || (user?.role === "stylist" && user?.subscription_tier === "pro")}
                    className={`${user?.role === "stylist" && user?.subscription_tier === "pro"
                      ? "bg-green-500/20 text-green-400 border border-green-500/50" 
                      : "bg-amber-500 hover:bg-amber-600 text-black"}`}
                  >
                    <Scissors className="w-4 h-4 mr-2" />
                    Stylist/Barber
                    <Badge className="ml-2 bg-black/20 text-xs">PRO</Badge>
                    {user?.role === "stylist" && user?.subscription_tier === "pro" && <CheckCircle className="w-4 h-4 ml-2" />}
                  </Button>
                  <Button
                    onClick={() => handleSwitchRole("owner", "elite")}
                    disabled={switchingRole || (user?.role === "owner" && user?.subscription_tier === "elite")}
                    className={`${user?.role === "owner" && user?.subscription_tier === "elite"
                      ? "bg-green-500/20 text-green-400 border border-green-500/50" 
                      : "bg-purple-500 hover:bg-purple-600 text-white"}`}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Salon Owner
                    <Badge className="ml-2 bg-white/20 text-xs">ELITE</Badge>
                    {user?.role === "owner" && user?.subscription_tier === "elite" && <CheckCircle className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </div>
              {switchingRole && (
                <div className="mt-4 flex items-center gap-2 text-amber-400">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Switching role...</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Share Demo Package Card */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-amber-400" />
                  Share Demo Package
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Share the demo with potential clients, stylists, or investors - no login required!
                </p>
                <p className="text-amber-400/70 text-xs mt-1">
                  Public link: {window.location.origin}/demo-package
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/demo-package`);
                    toast.success("Demo link copied to clipboard!");
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={() => {
                    const subject = encodeURIComponent("Check out MyHairChair - The Future of Salon Management");
                    const body = encodeURIComponent(`Hi,\n\nI wanted to share MyHairChair with you - it's the all-in-one platform for salon owners, stylists, and barbers.\n\nExplore the demo here: ${window.location.origin}/demo-package\n\nFeatures include:\n• Easy online booking\n• Payment processing\n• Loyalty rewards\n• Booth rent collection\n• And much more!\n\nLet me know what you think!`);
                    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                  }}
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button
                  onClick={() => {
                    const message = encodeURIComponent(`Check out MyHairChair - The all-in-one salon management platform! 💈✨\n\nExplore the demo: ${window.location.origin}/demo-package`);
                    window.open(`sms:?body=${message}`, '_blank');
                  }}
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  SMS
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Management Quick Access - Super Admin Only */}
        {isSuperAdmin && (
          <Card className="bg-gradient-to-br from-purple-500/10 to-amber-500/10 border-purple-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-400" />
                    Admin Team Management
                    <Badge className="bg-gradient-to-r from-purple-500 to-amber-500 text-white text-xs">HIERARCHY</Badge>
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Manage admins, super soldiers, permissions, and approval workflows
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-400">
                      <Users className="w-3 h-3 mr-1" />
                      Admins
                    </Badge>
                    <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">
                      <Shield className="w-3 h-3 mr-1" />
                      Super Soldiers
                    </Badge>
                    <Badge variant="outline" className="text-xs border-green-500/50 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approvals
                    </Badge>
                    <Badge variant="outline" className="text-xs border-blue-500/50 text-blue-400">
                      <FileText className="w-3 h-3 mr-1" />
                      Audit Logs
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate('/admin/team')}
                    className="bg-gradient-to-r from-purple-500 to-amber-500 hover:from-purple-600 hover:to-amber-600 text-white"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Open Team Dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Date Range Selector */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white">Platform Overview</h2>
            <p className="text-gray-400">Monitor users, subscriptions, and revenue</p>
          </div>
          <div className="flex items-center gap-2">
            {["7d", "30d", "90d", "1y"].map((range) => (
              <Button
                key={range}
                variant={dateRange === range ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(range)}
                className={dateRange === range 
                  ? "bg-red-500 hover:bg-red-600 text-white" 
                  : "border-stone-700 text-gray-400 hover:text-white"
                }
              >
                {range === "7d" ? "7 Days" : range === "30d" ? "30 Days" : range === "90d" ? "90 Days" : "1 Year"}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="border-stone-700 text-gray-400 hover:text-white">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Total Users" 
            value={stats?.totalUsers?.toLocaleString()} 
            change={12.5}
            trend="up"
            icon={Users}
            clickType="total_users"
            onClick={handleStatCardClick}
          />
          <StatCard 
            title="Active Subscriptions" 
            value={stats?.activeSubscriptions?.toLocaleString()} 
            change={18.5}
            trend="up"
            icon={CreditCard}
            clickType="active_subscriptions"
            onClick={handleStatCardClick}
          />
          <StatCard 
            title="Monthly Revenue" 
            value={`$${stats?.monthlyRevenue?.toLocaleString()}`} 
            change={stats?.revenueGrowth}
            trend="up"
            icon={DollarSign}
            color="green"
            clickType="active_subscriptions"
            onClick={handleStatCardClick}
          />
          <StatCard 
            title="Churn Rate" 
            value={`${stats?.churnRate}%`} 
            change={0.5}
            trend="down"
            icon={UserMinus}
            color="red" 
          />
        </div>

        {/* Site Traffic Analytics - Super Admin Only */}
        {adminPermissions?.is_super_admin && siteAnalytics && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-400" />
              Site Traffic Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-gray-400">Total Page Views</span>
                  </div>
                  <p className="text-2xl font-bold text-cyan-400">{siteAnalytics.total_views?.toLocaleString() || 0}</p>
                  {siteAnalytics.growth_rate !== 0 && (
                    <p className={`text-xs mt-1 ${siteAnalytics.growth_rate > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {siteAnalytics.growth_rate > 0 ? '↑' : '↓'} {Math.abs(siteAnalytics.growth_rate)}% vs prev period
                    </p>
                  )}
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-400">Views Today</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400">{siteAnalytics.views_today?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-gray-400">Views This Week</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-400">{siteAnalytics.views_this_week?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-gray-400">Unique Visitors</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-400">{siteAnalytics.unique_visitors?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Top Pages */}
            {siteAnalytics.page_breakdown?.length > 0 && (
              <Card className="bg-stone-900 border-stone-800 mt-4">
                <CardContent className="p-4">
                  <h4 className="text-sm font-semibold text-gray-400 mb-3">Top Pages</h4>
                  <div className="space-y-2">
                    {siteAnalytics.page_breakdown.slice(0, 5).map((page, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm text-white truncate max-w-[200px]">{page.page || '/'}</span>
                        <span className="text-sm text-cyan-400 font-medium">{page.views?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Subscription Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card 
            className="bg-stone-900 border-stone-800 cursor-pointer hover:bg-stone-800/50 hover:border-stone-700 transition-all"
            onClick={() => handleStatCardClick("free_users")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  Free Users
                  <Eye className="w-3 h-3 opacity-50" />
                </h3>
                <Badge className="bg-stone-700 text-gray-300">Free</Badge>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.freeUsers?.toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-1">
                {((stats?.freeUsers / stats?.totalUsers) * 100).toFixed(1)}% of total
              </p>
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30 cursor-pointer hover:from-amber-500/20 hover:to-yellow-500/20 transition-all"
            onClick={() => handleStatCardClick("pro_subscribers")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  Pro Subscribers
                  <Eye className="w-3 h-3 opacity-50" />
                </h3>
                <Badge className="bg-amber-500 text-black">Pro</Badge>
              </div>
              <p className="text-3xl font-bold text-amber-400">{stats?.proUsers?.toLocaleString()}</p>
              <p className="text-sm text-amber-400/70 mt-1">
                ${(stats?.proUsers * pricingSettings.pro_price).toLocaleString()}/mo revenue
              </p>
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 cursor-pointer hover:from-purple-500/20 hover:to-pink-500/20 transition-all"
            onClick={() => handleStatCardClick("elite_subscribers")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  Elite Subscribers
                  <Eye className="w-3 h-3 opacity-50" />
                </h3>
                <Badge className="bg-purple-500 text-white">Elite</Badge>
              </div>
              <p className="text-3xl font-bold text-purple-400">{stats?.eliteUsers?.toLocaleString()}</p>
              <p className="text-sm text-purple-400/70 mt-1">
                ${(stats?.eliteUsers * pricingSettings.elite_price).toLocaleString()}/mo revenue
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Activity Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card 
            className="bg-stone-900 border-stone-800 cursor-pointer hover:bg-stone-800/50 transition-all"
            onClick={() => handleStatCardClick("new_today")}
          >
            <CardContent className="p-4 text-center">
              <UserPlus className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.newUsersToday}</p>
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                New Today <Eye className="w-2 h-2 opacity-50" />
              </p>
            </CardContent>
          </Card>
          <Card 
            className="bg-stone-900 border-stone-800 cursor-pointer hover:bg-stone-800/50 transition-all"
            onClick={() => handleStatCardClick("renewals")}
          >
            <CardContent className="p-4 text-center">
              <RefreshCw className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.renewals}</p>
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                Renewals <Eye className="w-2 h-2 opacity-50" />
              </p>
            </CardContent>
          </Card>
          <Card className="bg-stone-900 border-stone-800">
            <CardContent className="p-4 text-center">
              <UserMinus className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.cancellations}</p>
              <p className="text-xs text-gray-400">Cancellations</p>
            </CardContent>
          </Card>
          <Card 
            className="bg-stone-900 border-stone-800 cursor-pointer hover:bg-stone-800/50 transition-all"
            onClick={() => handleStatCardClick("trial_to_paid")}
          >
            <CardContent className="p-4 text-center">
              <Sparkles className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.trialConversions}</p>
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                Trial → Paid <Eye className="w-2 h-2 opacity-50" />
              </p>
            </CardContent>
          </Card>
          <Card className="bg-stone-900 border-stone-800">
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.bookingsToday}</p>
              <p className="text-xs text-gray-400">Bookings Today</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Users, Subscriptions, Activity, Admin Management */}
        <Tabs defaultValue="users" className="space-y-6">
          <div className="overflow-x-auto pb-2">
            <TabsList className="bg-stone-900 border border-stone-800 inline-flex min-w-max">
            <TabsTrigger value="users" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="admins" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Manage Admins
              </TabsTrigger>
            )}
            <TabsTrigger value="invite" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
              <Share2 className="w-4 h-4 mr-2" />
              Invite Clients
              <Badge className="ml-2 bg-green-500 text-white text-xs">NEW</Badge>
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="messages" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                <Megaphone className="w-4 h-4 mr-2" />
                Messages
                <Badge className="ml-2 bg-blue-500 text-white text-xs">NEW</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger 
                value="soldiers" 
                className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"
                onClick={() => fetchSoldierApplications(soldierFilter)}
              >
                <Shield className="w-4 h-4 mr-2" />
                Soldiers
                <Badge className="ml-2 bg-amber-500 text-black text-xs">NEW</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger 
                value="team" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-amber-500 data-[state=active]:text-white border border-purple-500/30 hover:border-purple-500/60"
              >
                <Crown className="w-4 h-4 mr-2" />
                Team Management
                <Badge className="ml-2 bg-gradient-to-r from-purple-500 to-amber-500 text-white text-xs animate-pulse">ADMIN</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger 
                value="stripe" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-500 data-[state=active]:text-white border border-blue-500/30 hover:border-blue-500/60"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Stripe Fees
                <Badge className="ml-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs">LIVE</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger 
                value="promotions" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white border border-green-500/30 hover:border-green-500/60"
                onClick={() => fetchAdminPromotions()}
              >
                <Gift className="w-4 h-4 mr-2" />
                Promotions
                <Badge className="ml-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs">NEW</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger 
                value="userlookup" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-orange-500 data-[state=active]:text-white border border-red-500/30 hover:border-red-500/60"
              >
                <Search className="w-4 h-4 mr-2" />
                User Lookup
                <Badge className="ml-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs">ALL</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger 
                value="reports" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white border border-green-500/30 hover:border-green-500/60"
              >
                <BarChart className="w-4 h-4 mr-2" />
                Reports
                <Badge className="ml-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs">NEW</Badge>
              </TabsTrigger>
            )}
            <TabsTrigger value="verification" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <BadgeCheck className="w-4 h-4 mr-2" />
              Verification
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="lookfest" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                <Trophy className="w-4 h-4 mr-2" />
                Look Fest
                <Badge className="ml-2 bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs">CONTEST</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="beta" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                <Rocket className="w-4 h-4 mr-2" />
                Beta
                <Badge className="ml-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">LAUNCH</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="email-campaign" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-black border border-amber-500/30 hover:border-amber-500/60">
                <Mail className="w-4 h-4 mr-2" />
                Email Campaign
                <Badge className="ml-2 bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs">AI</Badge>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="settings" className="data-[state=active]:bg-stone-600 data-[state=active]:text-white">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </TabsTrigger>
            )}
            </TabsList>
          </div>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">All Users</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-stone-800 border-stone-700 text-white w-64"
                      />
                    </div>
                    <select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      className="px-3 py-2 bg-stone-800 border border-stone-700 rounded-md text-white text-sm"
                    >
                      <option value="all">All Roles</option>
                      <option value="client">Clients</option>
                      <option value="stylist">Stylists & Barbers</option>
                      <option value="owner">Salon Owners</option>
                    </select>
                    <select
                      value={filterTier}
                      onChange={(e) => setFilterTier(e.target.value)}
                      className="px-3 py-2 bg-stone-800 border border-stone-700 rounded-md text-white text-sm"
                    >
                      <option value="all">All Tiers</option>
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="elite">Elite</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 text-sm border-b border-stone-800">
                        <th className="pb-3 font-medium">User</th>
                        <th className="pb-3 font-medium">Role</th>
                        <th className="pb-3 font-medium">Tier</th>
                        <th className="pb-3 font-medium">Soldier</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Joined</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.user_id} className="border-b border-stone-800/50 hover:bg-stone-800/30">
                          <td className="py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-black font-bold">
                                {u.name?.charAt(0)}
                              </div>
                              <div>
                                <p className="text-white font-medium">{u.name}</p>
                                <p className="text-gray-500 text-sm">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4">
                            <Badge variant="outline" className="border-stone-700 text-gray-300 capitalize">
                              {u.role}
                            </Badge>
                          </td>
                          <td className="py-4">
                            <Badge className={
                              u.tier === "elite" ? "bg-purple-500 text-white" :
                              u.tier === "pro" ? "bg-amber-500 text-black" :
                              "bg-stone-700 text-gray-300"
                            }>
                              {u.tier?.charAt(0).toUpperCase() + u.tier?.slice(1)}
                            </Badge>
                          </td>
                          <td className="py-4">
                            {u.is_super_soldier ? (
                              <Badge className="bg-pink-500/20 text-pink-400">
                                <Zap className="w-3 h-3 mr-1" />
                                Super Soldier
                              </Badge>
                            ) : u.is_soldier ? (
                              u.soldier_status === 'active' ? (
                                <Badge className="bg-green-500/20 text-green-400">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Active
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-500/20 text-yellow-400">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>
                          <td className="py-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${u.status === "active" ? "bg-green-400" : "bg-gray-400"}`} />
                              <span className="text-gray-300 text-sm capitalize">{u.status}</span>
                            </div>
                          </td>
                          <td className="py-4 text-gray-400 text-sm">{u.created_at}</td>
                          <td className="py-4">
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-gray-400 hover:text-white"
                                onClick={() => handleViewUser(u.user_id)}
                                data-testid={`view-user-${u.user_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isSuperAdmin && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-amber-400 hover:text-amber-300"
                                    onClick={() => {
                                      handleViewUser(u.user_id);
                                      setTimeout(() => setShowSubscriptionModal(true), 500);
                                    }}
                                    data-testid={`manage-subscription-${u.user_id}`}
                                    title="Manage Subscription"
                                  >
                                    <Crown className="w-4 h-4" />
                                  </Button>
                                  {/* Make Soldier / Super Soldier */}
                                  {!u.is_soldier ? (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-purple-400 hover:text-purple-300"
                                      onClick={() => handleMakeSoldier(u.user_id, u.email, u.name)}
                                      data-testid={`make-soldier-${u.user_id}`}
                                      title="Make Soldier"
                                    >
                                      <Shield className="w-4 h-4" />
                                    </Button>
                                  ) : !u.is_super_soldier ? (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-pink-400 hover:text-pink-300"
                                      onClick={() => handleMakeSuperSoldier(u.user_id, u.email, u.name)}
                                      data-testid={`make-super-soldier-${u.user_id}`}
                                      title="Promote to Super Soldier"
                                    >
                                      <Zap className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <Badge className="bg-pink-500/20 text-pink-400 text-xs ml-1">
                                      Super Soldier
                                    </Badge>
                                  )}
                                  {u.status === "active" ? (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-red-400 hover:text-red-300"
                                      onClick={() => handleSuspendUser(u.user_id)}
                                      data-testid={`suspend-user-${u.user_id}`}
                                    >
                                      <ShieldAlert className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-green-400 hover:text-green-300"
                                      onClick={() => handleReactivateUser(u.user_id)}
                                      data-testid={`reactivate-user-${u.user_id}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-400" />
                      Active Subscriptions
                    </CardTitle>
                    <p className="text-gray-400 text-sm mt-1">Click on any user to view details, send messages, and manage subscription</p>
                  </div>
                  {/* Quick Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      placeholder="Search subscribers..."
                      value={subscriptionSearch || ""}
                      onChange={(e) => setSubscriptionSearch(e.target.value)}
                      className="bg-stone-800 border-stone-700 pl-10 w-64"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 text-sm border-b border-stone-800">
                        <th className="pb-3 font-medium">User</th>
                        <th className="pb-3 font-medium">Plan</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Started</th>
                        <th className="pb-3 font-medium">Next Billing</th>
                        <th className="pb-3 font-medium">Amount</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions
                        .filter(sub => !subscriptionSearch || 
                          sub.user?.toLowerCase().includes(subscriptionSearch.toLowerCase()) ||
                          sub.email?.toLowerCase().includes(subscriptionSearch.toLowerCase()))
                        .map((sub) => (
                        <tr 
                          key={sub.id} 
                          className="border-b border-stone-800/50 hover:bg-stone-800/50 cursor-pointer transition-colors"
                          onClick={() => handleViewSubscriber(sub)}
                          data-testid={`subscription-row-${sub.id}`}
                        >
                          <td className="py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                                {sub.user?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                              <div>
                                <p className="text-white font-medium">{sub.user || "Unknown"}</p>
                                <p className="text-gray-500 text-xs">{sub.email || sub.user_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4">
                            <Badge className={
                              sub.tier === "elite" ? "bg-purple-500 text-white" :
                              "bg-amber-500 text-black"
                            }>
                              {sub.tier?.charAt(0).toUpperCase() + sub.tier?.slice(1)}
                            </Badge>
                          </td>
                          <td className="py-4">
                            <Badge variant="outline" className={
                              sub.status === "active" ? "border-green-500 text-green-400" :
                              sub.status === "trial" ? "border-blue-500 text-blue-400" :
                              "border-red-500 text-red-400"
                            }>
                              {sub.status}
                            </Badge>
                          </td>
                          <td className="py-4 text-gray-400 text-sm">{sub.started || "N/A"}</td>
                          <td className="py-4 text-gray-400 text-sm">{sub.nextBilling || "N/A"}</td>
                          <td className="py-4 text-green-400 font-medium">${sub.amount || "0"}</td>
                          <td className="py-4">
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleViewSubscriber(sub)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleMessageSubscriber(sub)}
                                className="text-green-400 hover:text-green-300"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleEditSubscription(sub)}
                                className="text-amber-400 hover:text-amber-300"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {subscriptions.length === 0 && (
                    <div className="text-center py-12">
                      <Crown className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No active subscriptions found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader>
                <CardTitle className="text-white">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivity.map((activity, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-stone-800/50 rounded-xl">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        activity.type === "signup" ? "bg-green-500/20 text-green-400" :
                        activity.type === "subscription" ? "bg-amber-500/20 text-amber-400" :
                        activity.type === "cancellation" ? "bg-red-500/20 text-red-400" :
                        activity.type === "renewal" ? "bg-blue-500/20 text-blue-400" :
                        "bg-purple-500/20 text-purple-400"
                      }`}>
                        {activity.type === "signup" ? <UserPlus className="w-5 h-5" /> :
                         activity.type === "subscription" ? <Crown className="w-5 h-5" /> :
                         activity.type === "cancellation" ? <UserMinus className="w-5 h-5" /> :
                         activity.type === "renewal" ? <RefreshCw className="w-5 h-5" /> :
                         <Calendar className="w-5 h-5" />
                        }
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{activity.message}</p>
                        <p className="text-gray-500 text-sm">{activity.user}</p>
                      </div>
                      <p className="text-gray-500 text-sm">{activity.time}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Management Tab (Super Admin Only) */}
          {isSuperAdmin && (
            <TabsContent value="admins">
              <Card className="bg-stone-900 border-stone-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-red-400" />
                        Administrator Management
                      </CardTitle>
                      <p className="text-gray-400 text-sm mt-1">
                        Manage admin access and permission levels
                      </p>
                    </div>
                    <Button 
                      onClick={() => setShowAddAdmin(true)}
                      className="bg-red-500 hover:bg-red-600 text-white"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Admin
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Permission Levels Legend */}
                  <div className="mb-6 p-4 bg-stone-800/50 rounded-xl">
                    <h4 className="text-white font-medium mb-3">Permission Levels</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Badge className="bg-red-500 text-white mb-2">Super Admin</Badge>
                        <p className="text-xs text-gray-400">Full access to everything</p>
                      </div>
                      <div>
                        <Badge className="bg-purple-500 text-white mb-2">Admin</Badge>
                        <p className="text-xs text-gray-400">Stats, Users, Subscriptions, Activity, Manage Users</p>
                      </div>
                      <div>
                        <Badge className="bg-blue-500 text-white mb-2">Moderator</Badge>
                        <p className="text-xs text-gray-400">Stats, Users, Activity</p>
                      </div>
                      <div>
                        <Badge className="bg-green-500 text-white mb-2">Analyst</Badge>
                        <p className="text-xs text-gray-400">Stats, Subscriptions only</p>
                      </div>
                    </div>
                  </div>

                  {/* Admin List */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-gray-400 text-sm border-b border-stone-800">
                          <th className="pb-3 font-medium">Administrator</th>
                          <th className="pb-3 font-medium">Role</th>
                          <th className="pb-3 font-medium">Status</th>
                          <th className="pb-3 font-medium">Added</th>
                          <th className="pb-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {admins.map((admin) => (
                          <tr key={admin.email} className="border-b border-stone-800/50 hover:bg-stone-800/30">
                            <td className="py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                  admin.is_super ? "bg-red-500" : "bg-purple-500"
                                }`}>
                                  {admin.name?.charAt(0) || admin.email?.charAt(0)}
                                </div>
                                <div>
                                  <p className="text-white font-medium flex items-center gap-2">
                                    {admin.name || "Unknown"}
                                    {admin.is_super && (
                                      <Crown className="w-4 h-4 text-amber-400" />
                                    )}
                                  </p>
                                  <p className="text-gray-500 text-sm">{admin.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4">
                              <Badge className={getRoleBadgeColor(admin.admin_role)}>
                                {admin.admin_role?.replace("_", " ").toUpperCase()}
                              </Badge>
                            </td>
                            <td className="py-4">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${admin.active ? "bg-green-400" : "bg-red-400"}`} />
                                <span className="text-gray-300 text-sm">{admin.active ? "Active" : "Inactive"}</span>
                              </div>
                            </td>
                            <td className="py-4 text-gray-400 text-sm">
                              {admin.created_at?.slice(0, 10) || "-"}
                            </td>
                            <td className="py-4">
                              {!admin.is_super ? (
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => setShowEditAdmin(admin)}
                                    className="text-gray-400 hover:text-white"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleUpdateAdmin(admin.email, { active: !admin.active })}
                                    className={admin.active ? "text-amber-400 hover:text-amber-300" : "text-green-400 hover:text-green-300"}
                                  >
                                    {admin.active ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleRemoveAdmin(admin.email)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">Protected</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Invite Clients Tab */}
          <TabsContent value="invite">
            <ClientInviteTools 
              user={user} 
              isAdmin={true}
            />
          </TabsContent>

          {/* Messages Tab */}
          {isSuperAdmin && (
            <TabsContent value="messages">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                      <Megaphone className="w-6 h-6 text-blue-400" />
                      Send Messages to Users
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">
                      Broadcast messages to all users or specific groups via email
                    </p>
                  </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Message Templates */}
                  <div className="lg:col-span-1 space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <FileText className="w-5 h-5 text-amber-400" />
                      Quick Templates
                    </h3>
                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                      {messageTemplates.map((template) => (
                        <Card 
                          key={template.id}
                          className={`cursor-pointer transition-all hover:scale-[1.02] ${
                            selectedTemplate?.id === template.id 
                              ? 'ring-2 ring-blue-500 bg-blue-500/10' 
                              : 'bg-stone-800/50 hover:bg-stone-800'
                          } border-stone-700`}
                          onClick={() => {
                            setSelectedTemplate(template);
                            setMessageSubject(template.subject);
                            setMessageContent(template.content);
                          }}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${template.color} flex items-center justify-center`}>
                                <template.icon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="text-white font-semibold text-sm">{template.name}</h4>
                                <p className="text-gray-500 text-xs truncate max-w-[180px]">{template.subject}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Compose Message */}
                  <div className="lg:col-span-2">
                    <Card className="bg-stone-900 border-stone-800">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Mail className="w-5 h-5 text-blue-400" />
                          Compose Message
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Recipients */}
                        <div className="space-y-2">
                          <Label className="text-white">Recipients</Label>
                          <Select value={messageRecipients} onValueChange={setMessageRecipients}>
                            <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                              <SelectValue placeholder="Select recipients" />
                            </SelectTrigger>
                            <SelectContent className="bg-stone-800 border-stone-700">
                              <SelectItem value="all" className="text-white hover:bg-stone-700">
                                <div className="flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  All Users ({stats?.totalUsers || 0})
                                </div>
                              </SelectItem>
                              <SelectItem value="clients" className="text-white hover:bg-stone-700">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4" />
                                  Clients Only
                                </div>
                              </SelectItem>
                              <SelectItem value="stylists" className="text-white hover:bg-stone-700">
                                <div className="flex items-center gap-2">
                                  <Scissors className="w-4 h-4" />
                                  Stylists/Barbers Only
                                </div>
                              </SelectItem>
                              <SelectItem value="owners" className="text-white hover:bg-stone-700">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4" />
                                  Salon Owners Only
                                </div>
                              </SelectItem>
                              <SelectItem value="pro" className="text-white hover:bg-stone-700">
                                <div className="flex items-center gap-2">
                                  <Crown className="w-4 h-4 text-amber-400" />
                                  Pro Subscribers
                                </div>
                              </SelectItem>
                              <SelectItem value="elite" className="text-white hover:bg-stone-700">
                                <div className="flex items-center gap-2">
                                  <Crown className="w-4 h-4 text-purple-400" />
                                  Elite Subscribers
                                </div>
                              </SelectItem>
                              <SelectItem value="beta" className="text-white hover:bg-stone-700">
                                <div className="flex items-center gap-2">
                                  <Gift className="w-4 h-4 text-green-400" />
                                  Beta Testers
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Subject */}
                        <div className="space-y-2">
                          <Label className="text-white">Subject Line</Label>
                          <Input
                            value={messageSubject}
                            onChange={(e) => setMessageSubject(e.target.value)}
                            placeholder="Enter email subject..."
                            className="bg-stone-800 border-stone-700 text-white"
                          />
                        </div>

                        {/* Message Content */}
                        <div className="space-y-2">
                          <Label className="text-white">Message Content</Label>
                          <Textarea
                            value={messageContent}
                            onChange={(e) => setMessageContent(e.target.value)}
                            placeholder="Write your message here..."
                            className="bg-stone-800 border-stone-700 text-white min-h-[300px] font-mono text-sm"
                          />
                        </div>

                        {/* Preview */}
                        {messageContent && (
                          <div className="space-y-2">
                            <Label className="text-white flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              Preview
                            </Label>
                            <div className="bg-white rounded-lg p-4 text-gray-800 max-h-[200px] overflow-y-auto">
                              <div className="border-b pb-2 mb-3">
                                <p className="font-bold text-lg">{messageSubject || "No subject"}</p>
                              </div>
                              <div className="whitespace-pre-wrap text-sm">
                                {messageContent}
                              </div>
                            </div>
                            {/* Copy for Email Buttons */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {/* Copy with HTML formatting for rich-text email clients */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                                data-testid="copy-for-email-btn"
                                onClick={async () => {
                                  try {
                                    // Check if the selected template has HTML content
                                    const htmlContent = selectedTemplate?.htmlContent;
                                    
                                    if (htmlContent) {
                                      // Copy HTML to clipboard (preserves formatting in email clients)
                                      const blob = new Blob([htmlContent], { type: 'text/html' });
                                      const clipboardItem = new ClipboardItem({ 'text/html': blob });
                                      await navigator.clipboard.write([clipboardItem]);
                                      toast.success("Copied with formatting! Paste into Gmail, Outlook, or any email client.");
                                    } else {
                                      // Fallback: Convert plain text to basic HTML
                                      const htmlFromPlain = messageContent
                                        .split('\n\n')
                                        .map(paragraph => {
                                          // Check if it's a section header (all caps)
                                          if (paragraph === paragraph.toUpperCase() && paragraph.length > 3 && !paragraph.startsWith('•')) {
                                            return `<p><strong>${paragraph}</strong></p>`;
                                          }
                                          // Check if it's a bullet point
                                          if (paragraph.startsWith('•')) {
                                            return `<p>${paragraph}</p>`;
                                          }
                                          return `<p>${paragraph}</p>`;
                                        })
                                        .join('\n');
                                      
                                      const blob = new Blob([htmlFromPlain], { type: 'text/html' });
                                      const clipboardItem = new ClipboardItem({ 'text/html': blob });
                                      await navigator.clipboard.write([clipboardItem]);
                                      toast.success("Copied with formatting! Paste into Gmail, Outlook, or any email client.");
                                    }
                                  } catch (error) {
                                    console.error("Clipboard write failed:", error);
                                    // Fallback to plain text copy
                                    navigator.clipboard.writeText(messageContent);
                                    toast.info("Copied as plain text. For best results, use Chrome or Edge.");
                                  }
                                }}
                              >
                                <Copy className="w-4 h-4 mr-2" />
                                Copy for Email
                              </Button>
                              
                              {/* Copy plain text (for simple pasting) */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-stone-600 text-gray-300 hover:bg-stone-700"
                                onClick={() => {
                                  navigator.clipboard.writeText(messageContent);
                                  toast.success("Copied as plain text!");
                                }}
                              >
                                <Copy className="w-4 h-4 mr-2" />
                                Copy Plain Text
                              </Button>
                              
                              {/* Open in email client */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                                onClick={() => {
                                  const subject = encodeURIComponent(messageSubject);
                                  const body = encodeURIComponent(messageContent);
                                  window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                                }}
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Open in Email App
                              </Button>
                            </div>
                            
                            {/* Tip for using the copy feature */}
                            {selectedTemplate?.htmlContent && (
                              <p className="text-xs text-emerald-400/70 mt-2">
                                💡 Tip: &quot;Copy for Email&quot; preserves bold headings, bullet points, and links when pasted into Gmail or Outlook.
                              </p>
                            )}
                          </div>
                        )}

                        {/* Send Button */}
                        <div className="flex items-center justify-between pt-4 border-t border-stone-700">
                          <div className="text-sm text-gray-400">
                            {messageRecipients === "all" && `Sending to ${stats?.totalUsers || 0} users`}
                            {messageRecipients === "clients" && "Sending to all clients"}
                            {messageRecipients === "stylists" && "Sending to all stylists/barbers"}
                            {messageRecipients === "owners" && "Sending to all salon owners"}
                            {messageRecipients === "pro" && `Sending to ${stats?.proUsers || 0} Pro subscribers`}
                            {messageRecipients === "elite" && `Sending to ${stats?.eliteUsers || 0} Elite subscribers`}
                            {messageRecipients === "beta" && "Sending to all beta testers"}
                          </div>
                          <Button
                            onClick={async () => {
                              if (!messageSubject || !messageContent) {
                                toast.error("Please enter a subject and message");
                                return;
                              }
                              setSendingMessage(true);
                              try {
                                const token = localStorage.getItem("token");
                                const res = await axios.post(`${API}/admin/broadcast-message`, {
                                  recipients: messageRecipients,
                                  subject: messageSubject,
                                  content: messageContent
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success(`Message sent to ${res.data.sent_count} users!`);
                                // Clear form
                                setMessageSubject("");
                                setMessageContent("");
                                setSelectedTemplate(null);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Failed to send message");
                              } finally {
                                setSendingMessage(false);
                              }
                            }}
                            disabled={sendingMessage || !messageSubject || !messageContent}
                            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8"
                          >
                            {sendingMessage ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send Message
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Reports Tab */}
          {isSuperAdmin && (
            <TabsContent value="reports">
              <Tabs defaultValue="summary" className="space-y-4">
                <TabsList className="bg-stone-800/50 border border-stone-700">
                  <TabsTrigger value="summary" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
                    📊 Platform Summary
                  </TabsTrigger>
                  <TabsTrigger value="revenue" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
                    💰 Revenue
                  </TabsTrigger>
                  <TabsTrigger value="soldiers" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
                    🎖️ Soldier Program
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="summary">
                  <ReportsDashboard 
                    token={token} 
                    reportType="admin" 
                    title="Platform Summary"
                  />
                </TabsContent>
                
                <TabsContent value="revenue">
                  <ReportsDashboard 
                    token={token} 
                    reportType="admin_revenue" 
                    title="Revenue Report"
                  />
                </TabsContent>
                
                <TabsContent value="soldiers">
                  <ReportsDashboard 
                    token={token} 
                    reportType="admin_soldiers" 
                    title="Soldier Program Performance"
                  />
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

          {/* HairChair Soldiers Tab */}
          {isSuperAdmin && (
            <TabsContent value="soldiers">
              <Tabs defaultValue="portal" className="space-y-4">
                <TabsList className="bg-stone-800/50 border border-stone-700 flex-wrap">
                  <TabsTrigger value="portal" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    🎖️ Command Center
                  </TabsTrigger>
                  <TabsTrigger value="recruitment" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                    📣 Recruitment Cards
                  </TabsTrigger>
                  <TabsTrigger value="agreement" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                    📜 Agreement
                  </TabsTrigger>
                  <TabsTrigger value="payouts" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
                    💰 Payouts
                  </TabsTrigger>
                  <TabsTrigger value="superSoldier" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                    👑 Super Soldiers
                  </TabsTrigger>
                  <TabsTrigger value="accountants" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black">
                    🧮 Accountants
                  </TabsTrigger>
                  <TabsTrigger value="applications" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    📋 Applications
                  </TabsTrigger>
                </TabsList>

                {/* Soldier Portal (Command Center) */}
                <TabsContent value="portal">
                  <AdminSoldierPortal token={token} />
                </TabsContent>

                {/* Recruitment Cards Tab */}
                <TabsContent value="recruitment">
                  <SoldierRecruitmentCards />
                </TabsContent>

                {/* Agreement Management Tab - NEW */}
                <TabsContent value="agreement">
                  <SoldierAgreementManager token={token} />
                </TabsContent>

                {/* Payouts Tab - NEW */}
                <TabsContent value="payouts">
                  <SoldierPayoutDashboard token={token} />
                </TabsContent>

                {/* Super Soldier Payouts & Platoon Management */}
                <TabsContent value="superSoldier">
                  <SuperSoldierPayoutDashboard />
                </TabsContent>

                {/* Accountants Tab - NEW */}
                <TabsContent value="accountants">
                  <AccountantManagement token={token} />
                </TabsContent>

                {/* Applications Tab (existing functionality) */}
                <TabsContent value="applications">
                  <div className="space-y-6">
                    {/* Soldiers Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border-amber-500/30">
                        <CardContent className="p-4 text-center">
                          <Shield className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-white">
                            {soldierStats?.applications?.approved || 0} / 50
                          </p>
                          <p className="text-xs text-amber-400">Active Soldiers</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-stone-900 border-stone-800">
                        <CardContent className="p-4 text-center">
                          <Clock className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-white">
                            {soldierStats?.applications?.pending || 0}
                          </p>
                          <p className="text-xs text-gray-400">Pending Review</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-stone-900 border-stone-800">
                        <CardContent className="p-4 text-center">
                          <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-white">
                            {soldierStats?.referrals?.total || 0}
                          </p>
                          <p className="text-xs text-gray-400">Total Referrals</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-stone-900 border-stone-800">
                        <CardContent className="p-4 text-center">
                          <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-white">
                            ${soldierStats?.payouts?.total_paid?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-gray-400">Total Paid Out</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Applications Table */}
                    <Card className="bg-stone-900 border-stone-800">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white flex items-center gap-2">
                            <Shield className="w-5 h-5 text-amber-400" />
                            Soldier Applications
                          </CardTitle>
                          <div className="flex gap-2">
                            {["pending", "approved", "denied", "waitlisted", "all"].map(status => (
                              <Button
                                key={status}
                                size="sm"
                                variant={soldierFilter === status ? "default" : "outline"}
                                onClick={() => {
                                  setSoldierFilter(status);
                                  fetchSoldierApplications(status);
                                }}
                                className={soldierFilter === status 
                                  ? "bg-amber-500 text-black hover:bg-amber-600" 
                                  : "border-stone-700 text-gray-400"
                                }
                              >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {soldierLoading ? (
                          <div className="text-center py-8 text-gray-400">Loading...</div>
                        ) : soldierApplications.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            No {soldierFilter !== "all" ? soldierFilter : ""} applications
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-stone-800">
                                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Salon</th>
                                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Owner</th>
                                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Location</th>
                                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Chairs</th>
                                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {soldierApplications.map(app => (
                                  <tr key={app.application_id} className="border-b border-stone-800/50 hover:bg-stone-800/50">
                                    <td className="py-3 px-4">
                                      <div className="text-white font-medium">{app.salon_name}</div>
                                      <div className="text-gray-500 text-xs">{app.email}</div>
                                    </td>
                                    <td className="py-3 px-4 text-gray-300">{app.owner_name}</td>
                                    <td className="py-3 px-4 text-gray-400">{app.city}, {app.state}</td>
                                    <td className="py-3 px-4 text-gray-300">{app.num_chairs}</td>
                                    <td className="py-3 px-4">
                                      <Badge className={
                                        app.status === "approved" ? "bg-green-500/20 text-green-400" :
                                        app.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                                        app.status === "denied" ? "bg-red-500/20 text-red-400" :
                                        "bg-blue-500/20 text-blue-400"
                                      }>
                                        {app.status}
                                      </Badge>
                                    </td>
                                    <td className="py-3 px-4 text-gray-400 text-xs">
                                      {new Date(app.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                      {app.status === "pending" && (
                                        <div className="flex gap-2 justify-end">
                                          <Button
                                            size="sm"
                                            onClick={() => handleApproveSoldier(app.application_id)}
                                            className="bg-green-600 hover:bg-green-700 text-white"
                                          >
                                            <CheckCircle className="w-4 h-4" />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => handleDenySoldier(app.application_id)}
                                          >
                                            <AlertCircle className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      )}
                                      {app.status === "approved" && app.referral_code && (
                                        <span className="text-amber-400 font-mono text-xs">{app.referral_code}</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

          {/* Admin Team Management Tab */}
          {isSuperAdmin && (
            <TabsContent value="team">
              <AdminTeamManagement />
            </TabsContent>
          )}

          {/* Stripe Fee Settings Tab */}
          {isSuperAdmin && (
            <TabsContent value="stripe">
              <div className="space-y-6">
                <StripeFeeSettings token={token} />
                <SoldiersSettingsEditor token={token} isSuperAdmin={true} canEdit={true} />
              </div>
            </TabsContent>
          )}

          {/* Promotions Management Tab */}
          {isSuperAdmin && (
            <TabsContent value="promotions">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <Gift className="w-6 h-6 text-green-400" />
                      Premium Promotions
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">Create and manage free premium access promotions</p>
                  </div>
                  <Button
                    onClick={() => setShowCreatePromo(true)}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Promotion
                  </Button>
                </div>

                {/* Create Promotion Dialog */}
                <Dialog open={showCreatePromo} onOpenChange={setShowCreatePromo}>
                  <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Gift className="w-5 h-5 text-green-400" />
                        Create New Promotion
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-gray-300">Promotion Name *</Label>
                        <Input
                          value={newPromo.name}
                          onChange={(e) => setNewPromo({...newPromo, name: e.target.value})}
                          placeholder="e.g., Holiday Special, New User Bonus"
                          className="bg-stone-800 border-stone-700 text-white mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-gray-300">Description</Label>
                        <Input
                          value={newPromo.description}
                          onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
                          placeholder="Brief description of the promotion"
                          className="bg-stone-800 border-stone-700 text-white mt-1"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300">Tier *</Label>
                          <select
                            value={newPromo.tier}
                            onChange={(e) => setNewPromo({...newPromo, tier: e.target.value})}
                            className="w-full bg-stone-800 border border-stone-700 text-white rounded-md p-2 mt-1"
                          >
                            <option value="pro">Pro ($29.99/mo value)</option>
                            <option value="elite">Elite ($49.99/mo value)</option>
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-gray-300">Duration (Days) *</Label>
                          <Input
                            type="number"
                            value={newPromo.duration_days}
                            onChange={(e) => setNewPromo({...newPromo, duration_days: e.target.value})}
                            min="1"
                            max="365"
                            className="bg-stone-800 border-stone-700 text-white mt-1"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300">Promo Code (auto if empty)</Label>
                          <Input
                            value={newPromo.promo_code}
                            onChange={(e) => setNewPromo({...newPromo, promo_code: e.target.value.toUpperCase()})}
                            placeholder="AUTO-GENERATE"
                            className="bg-stone-800 border-stone-700 text-white mt-1 uppercase"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-gray-300">Max Redemptions</Label>
                          <Input
                            type="number"
                            value={newPromo.max_redemptions || ""}
                            onChange={(e) => setNewPromo({...newPromo, max_redemptions: e.target.value})}
                            placeholder="Unlimited"
                            min="1"
                            className="bg-stone-800 border-stone-700 text-white mt-1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-gray-300">End Date (optional)</Label>
                        <Input
                          type="date"
                          value={newPromo.end_date}
                          onChange={(e) => setNewPromo({...newPromo, end_date: e.target.value})}
                          className="bg-stone-800 border-stone-700 text-white mt-1"
                        />
                      </div>
                      
                      {/* Preview */}
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <p className="text-green-400 text-sm font-medium mb-2">Preview:</p>
                        <p className="text-white">
                          🎉 <strong>{newPromo.name || "Promotion Name"}</strong> - {newPromo.duration_days} days of FREE {newPromo.tier?.charAt(0).toUpperCase() + newPromo.tier?.slice(1)} access!
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setShowCreatePromo(false)}
                        className="flex-1 border-stone-700 text-gray-300"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreatePromotion}
                        disabled={promoLoading}
                        className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500"
                      >
                        {promoLoading ? "Creating..." : "Create Promotion"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Promotions List */}
                <Card className="bg-stone-900 border-stone-800">
                  <CardContent className="p-0">
                    {promoLoading && adminPromotions.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        Loading promotions...
                      </div>
                    ) : adminPromotions.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No promotions created yet</p>
                        <p className="text-sm mt-2">Create your first promotion to offer free premium access!</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-stone-800">
                        {adminPromotions.map((promo) => (
                          <div key={promo.promotion_id} className="p-4 hover:bg-stone-800/50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                  promo.status === 'active' 
                                    ? 'bg-green-500/20' 
                                    : promo.status === 'expired' 
                                      ? 'bg-red-500/20' 
                                      : 'bg-gray-500/20'
                                }`}>
                                  <Gift className={`w-6 h-6 ${
                                    promo.status === 'active' 
                                      ? 'text-green-400' 
                                      : promo.status === 'expired' 
                                        ? 'text-red-400' 
                                        : 'text-gray-400'
                                  }`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-white">{promo.name}</h4>
                                    <Badge className={
                                      promo.status === 'active' 
                                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                        : promo.status === 'expired'
                                          ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                          : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                    }>
                                      {promo.status?.toUpperCase()}
                                    </Badge>
                                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                      {promo.tier?.toUpperCase()}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-400">{promo.description || "No description"}</p>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {promo.duration_days} days
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {promo.current_redemptions || 0} / {promo.max_redemptions || "∞"} used
                                    </span>
                                    <span className="font-mono bg-stone-800 px-2 py-0.5 rounded text-green-400">
                                      {promo.promo_code}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    navigator.clipboard.writeText(promo.promo_code);
                                    toast.success("Promo code copied!");
                                  }}
                                  className="text-gray-400 hover:text-white"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleTogglePromotion(promo.promotion_id)}
                                  className={promo.is_active ? "text-green-400" : "text-gray-400"}
                                >
                                  {promo.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeletePromotion(promo.promotion_id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Quick Presets */}
                <Card className="bg-stone-900 border-stone-800">
                  <CardHeader>
                    <CardTitle className="text-white text-sm">Quick Presets</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { name: "7-Day Pro Trial", tier: "pro", days: 7 },
                        { name: "14-Day Pro Trial", tier: "pro", days: 14 },
                        { name: "30-Day Pro Trial", tier: "pro", days: 30 },
                        { name: "7-Day Elite Trial", tier: "elite", days: 7 },
                      ].map((preset, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          onClick={() => {
                            setNewPromo({
                              ...newPromo,
                              name: preset.name,
                              tier: preset.tier,
                              duration_days: preset.days
                            });
                            setShowCreatePromo(true);
                          }}
                          className="border-stone-700 text-gray-300 hover:border-green-500 hover:text-green-400"
                        >
                          {preset.name}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* User Lookup Tab */}
          {isSuperAdmin && (
            <TabsContent value="userlookup">
              <SuperAdminUserManager token={token} />
            </TabsContent>
          )}

          {/* Verification Tab */}
          <TabsContent value="verification">
            <VerificationDashboard token={token} />
          </TabsContent>

          {/* Look Fest Contest Tab */}
          {isSuperAdmin && (
            <TabsContent value="lookfest">
              <LookFestAdmin />
            </TabsContent>
          )}

          {/* Beta Management Tab */}
          {isSuperAdmin && (
            <TabsContent value="beta">
              <BetaManagement />
            </TabsContent>
          )}

          {/* Email Campaign Tab - AI-Powered Corporate Communications */}
          {isSuperAdmin && (
            <TabsContent value="email-campaign">
              <EmailCampaignTab />
            </TabsContent>
          )}

          {/* Settings Tab - Pricing Management */}
          {isSuperAdmin && (
            <TabsContent value="settings">
              <div className="space-y-6">
                {/* Pricing Settings Card */}
                <Card className="bg-stone-900 border-stone-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      Subscription Pricing
                    </CardTitle>
                    <p className="text-sm text-gray-400">
                      Adjust subscription prices in real-time. Changes will apply to new subscriptions immediately.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {pricingLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 text-amber-400 animate-spin" />
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Pro Tier Pricing */}
                        <div className="p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-4">
                            <Badge className="bg-amber-500 text-black">Pro</Badge>
                            <span className="text-white font-medium">Barber/Stylist Plan</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-gray-300 text-sm">Monthly Price ($)</Label>
                              <div className="relative mt-1">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={pricingSettings.pro_price}
                                  onChange={(e) => setPricingSettings({
                                    ...pricingSettings,
                                    pro_price: parseFloat(e.target.value) || 0
                                  })}
                                  className="pl-10 bg-stone-800 border-stone-700 text-white"
                                  data-testid="pro-price-input"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-gray-300 text-sm">Trial Days</Label>
                              <Input
                                type="number"
                                min="0"
                                value={pricingSettings.pro_trial_days}
                                onChange={(e) => setPricingSettings({
                                  ...pricingSettings,
                                  pro_trial_days: parseInt(e.target.value) || 0
                                })}
                                className="mt-1 bg-stone-800 border-stone-700 text-white"
                                data-testid="pro-trial-input"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Elite Tier Pricing */}
                        <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-4">
                            <Badge className="bg-purple-500 text-white">Elite</Badge>
                            <span className="text-white font-medium">Salon/Shop Owner Plan</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-gray-300 text-sm">Monthly Price ($)</Label>
                              <div className="relative mt-1">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={pricingSettings.elite_price}
                                  onChange={(e) => setPricingSettings({
                                    ...pricingSettings,
                                    elite_price: parseFloat(e.target.value) || 0
                                  })}
                                  className="pl-10 bg-stone-800 border-stone-700 text-white"
                                  data-testid="elite-price-input"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-gray-300 text-sm">Trial Days</Label>
                              <Input
                                type="number"
                                min="0"
                                value={pricingSettings.elite_trial_days}
                                onChange={(e) => setPricingSettings({
                                  ...pricingSettings,
                                  elite_trial_days: parseInt(e.target.value) || 0
                                })}
                                className="mt-1 bg-stone-800 border-stone-700 text-white"
                                data-testid="elite-trial-input"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Current Pricing Preview */}
                        <div className="p-4 bg-stone-800 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-400 mb-3">Current Pricing Preview</h4>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-xs text-gray-500">Free</p>
                              <p className="text-xl font-bold text-white">$0</p>
                              <p className="text-xs text-gray-500">forever</p>
                            </div>
                            <div className="border-l border-r border-stone-700">
                              <p className="text-xs text-amber-400">Pro</p>
                              <p className="text-xl font-bold text-amber-400">${pricingSettings.pro_price}</p>
                              <p className="text-xs text-gray-500">{pricingSettings.pro_trial_days}-day trial</p>
                            </div>
                            <div>
                              <p className="text-xs text-purple-400">Elite</p>
                              <p className="text-xl font-bold text-purple-400">${pricingSettings.elite_price}</p>
                              <p className="text-xs text-gray-500">{pricingSettings.elite_trial_days}-day trial</p>
                            </div>
                          </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end gap-3">
                          <Button
                            variant="outline"
                            onClick={fetchPricingSettings}
                            className="border-stone-600 text-gray-300 hover:bg-stone-800"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reset
                          </Button>
                          <Button
                            onClick={handleSavePricing}
                            disabled={savingPricing}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            data-testid="save-pricing-btn"
                          >
                            {savingPricing ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Save Pricing
                              </>
                            )}
                          </Button>
                        </div>

                        {/* Info Note */}
                        <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-300">
                            <p className="font-medium mb-1">Important Notes:</p>
                            <ul className="list-disc list-inside text-blue-300/80 space-y-1">
                              <li>Price changes apply to <strong>new subscriptions only</strong></li>
                              <li>Existing subscribers keep their current pricing</li>
                              <li>The membership page will display updated prices immediately</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Stripe Fee Settings */}
                <StripeFeeSettings />
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Subscriber Detail Modal */}
        <Dialog open={showSubscriberModal} onOpenChange={setShowSubscriberModal}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {viewingSubscriber?.user?.charAt(0)?.toUpperCase() || viewingSubscriber?.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <span>{viewingSubscriber?.user || viewingSubscriber?.name || "Subscriber"}</span>
                  <p className="text-sm text-gray-400 font-normal">{viewingSubscriber?.email}</p>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            {viewingSubscriber && (
              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="bg-stone-800 w-full">
                  <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                  <TabsTrigger value="subscription" className="flex-1">Subscription</TabsTrigger>
                  <TabsTrigger value="access" className="flex-1">Access Log</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-stone-800/50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="text-white">{viewingSubscriber.email || viewingSubscriber.user?.email || "N/A"}</p>
                    </div>
                    <div className="p-3 bg-stone-800/50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Phone</p>
                      <p className="text-white">{viewingSubscriber.phone || viewingSubscriber.user?.phone || "N/A"}</p>
                    </div>
                    <div className="p-3 bg-stone-800/50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Role</p>
                      <p className="text-white capitalize">{viewingSubscriber.role || viewingSubscriber.user?.role || "stylist"}</p>
                    </div>
                    <div className="p-3 bg-stone-800/50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">User ID</p>
                      <p className="text-gray-400 text-xs font-mono">{viewingSubscriber.user_id || viewingSubscriber.id}</p>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-4 border-t border-stone-800">
                    <Button 
                      onClick={() => { setShowSubscriberModal(false); handleMessageSubscriber(viewingSubscriber); }}
                      className="bg-green-500 hover:bg-green-600 text-white flex-1"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                    <Button 
                      onClick={() => { setShowSubscriberModal(false); handleEditSubscription(viewingSubscriber); }}
                      className="bg-amber-500 hover:bg-amber-600 text-black flex-1"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Subscription
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="subscription" className="space-y-4 mt-4">
                  <div className="p-4 bg-stone-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-400">Current Plan</span>
                      <Badge className={viewingSubscriber.tier === "elite" ? "bg-purple-500" : "bg-amber-500 text-black"}>
                        {viewingSubscriber.tier?.toUpperCase() || "FREE"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-400">Status</span>
                      <Badge variant="outline" className={
                        viewingSubscriber.status === "active" ? "border-green-500 text-green-400" :
                        viewingSubscriber.status === "trial" ? "border-blue-500 text-blue-400" :
                        "border-red-500 text-red-400"
                      }>
                        {viewingSubscriber.status || "active"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-400">Amount</span>
                      <span className="text-green-400 font-bold">${viewingSubscriber.amount || "0"}/mo</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Next Billing</span>
                      <span className="text-white">{viewingSubscriber.nextBilling || "N/A"}</span>
                    </div>
                  </div>

                  {/* Subscription Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      className="border-zinc-600"
                      onClick={() => handleUpdateSubscriptionFromModal(viewingSubscriber.user_id, "free", "downgrade")}
                    >
                      Set to Free
                    </Button>
                    <Button 
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                      onClick={() => handleUpdateSubscriptionFromModal(viewingSubscriber.user_id, "pro", "upgrade")}
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Upgrade to Pro
                    </Button>
                    <Button 
                      className="bg-purple-500 hover:bg-purple-600 text-white"
                      onClick={() => handleUpdateSubscriptionFromModal(viewingSubscriber.user_id, "elite", "upgrade")}
                    >
                      <Crown className="w-4 h-4 mr-1" />
                      Upgrade to Elite
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-green-500 text-green-400"
                      onClick={() => handleUpdateSubscriptionFromModal(viewingSubscriber.user_id, null, "extend")}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Extend 30 Days
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="access" className="mt-4">
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {accessLogs.length > 0 ? accessLogs.map((log, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-lg">
                        <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                          <Eye className="w-4 h-4 text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm">{log.action}</p>
                          <p className="text-gray-500 text-xs">{log.admin_email} - {new Date(log.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    )) : (
                      <p className="text-gray-500 text-center py-8">No access logs recorded</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Send Message Modal */}
        <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-400" />
                Send Message to {viewingSubscriber?.user || viewingSubscriber?.name}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Send an email to this subscriber
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-300">Subject</Label>
                <Input
                  value={subscriberMessage.subject}
                  onChange={(e) => setSubscriberMessage(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Message subject..."
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Message</Label>
                <Textarea
                  value={subscriberMessage.body}
                  onChange={(e) => setSubscriberMessage(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Type your message..."
                  className="bg-stone-800 border-stone-700 text-white mt-1 min-h-[150px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowMessageModal(false)} className="text-gray-400">
                Cancel
              </Button>
              <Button 
                onClick={handleSendSubscriberMessage} 
                disabled={sendingMessage}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {sendingMessage ? "Sending..." : "Send Message"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stat Card Users Modal */}
        <Dialog open={showStatCardModal} onOpenChange={setShowStatCardModal}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-3xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-400" />
                {statCardTitle} ({statCardUsers.length} users)
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Click on any user to view full details
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[60vh] pr-2">
              {statCardUsers.length > 0 ? (
                <div className="space-y-2">
                  {statCardUsers.map((user, idx) => (
                    <div
                      key={user.user_id || idx}
                      className="flex items-center justify-between p-4 bg-stone-800/50 rounded-lg hover:bg-stone-800 cursor-pointer transition-colors"
                      onClick={() => {
                        setShowStatCardModal(false);
                        handleViewSubscriber({
                          ...user,
                          user_id: user.user_id,
                          user: user.name,
                          email: user.email
                        });
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                          {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name || "No Name"}</p>
                          <p className="text-gray-400 text-sm">{user.email}</p>
                          {user.phone && <p className="text-gray-500 text-xs">{user.phone}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={
                          user.membership_tier === "elite" ? "bg-purple-500" :
                          user.membership_tier === "pro" ? "bg-amber-500 text-black" :
                          "bg-stone-700"
                        }>
                          {user.membership_tier || "free"}
                        </Badge>
                        <Badge variant="outline" className={
                          user.role === "owner" ? "border-purple-500 text-purple-400" :
                          user.role === "stylist" ? "border-amber-500 text-amber-400" :
                          "border-blue-500 text-blue-400"
                        }>
                          {user.role || "client"}
                        </Badge>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No users found in this category</p>
                  <p className="text-gray-500 text-sm mt-2">Try using the User Lookup tab for advanced search</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Admin Dialog */}
        <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-red-400" />
                Add New Administrator
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-300">Email Address</Label>
                <Input
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  placeholder="admin@example.com"
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Name</Label>
                <Input
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Permission Level</Label>
                <select
                  value={newAdmin.admin_role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, admin_role: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-md text-white"
                >
                  <option value="analyst">Analyst (Stats & Subscriptions only)</option>
                  <option value="moderator">Moderator (Stats, Users, Activity)</option>
                  <option value="admin">Admin (Full access except admin management)</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowAddAdmin(false)} className="text-gray-400">
                Cancel
              </Button>
              <Button onClick={handleAddAdmin} className="bg-red-500 hover:bg-red-600 text-white">
                Add Administrator
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Admin Dialog */}
        <Dialog open={!!showEditAdmin} onOpenChange={() => setShowEditAdmin(null)}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-amber-400" />
                Edit Administrator
              </DialogTitle>
            </DialogHeader>
            {showEditAdmin && (
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-gray-300">Email</Label>
                  <p className="text-white font-medium">{showEditAdmin.email}</p>
                </div>
                <div>
                  <Label className="text-gray-300">Name</Label>
                  <p className="text-white">{showEditAdmin.name}</p>
                </div>
                <div>
                  <Label className="text-gray-300">Permission Level</Label>
                  <select
                    defaultValue={showEditAdmin.admin_role}
                    onChange={(e) => handleUpdateAdmin(showEditAdmin.email, { admin_role: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-md text-white"
                  >
                    <option value="analyst">Analyst</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowEditAdmin(null)} className="text-gray-400">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Details Dialog */}
        <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                User Details
              </DialogTitle>
            </DialogHeader>
            {selectedUser?.user ? (
              <div className="space-y-6 py-4">
                {/* User Info */}
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-black text-2xl font-bold">
                    {selectedUser.user.name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">{selectedUser.user.name || "Unknown"}</h3>
                    <p className="text-gray-400">{selectedUser.user.email || "No email"}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={
                        selectedUser.user.role === "owner" ? "bg-purple-500" :
                        selectedUser.user.role === "stylist" ? "bg-amber-500 text-black" :
                        "bg-blue-500"
                      }>
                        {selectedUser.user.role || "client"}
                      </Badge>
                      <Badge className={
                        selectedUser.user.membership?.tier === "elite" ? "bg-purple-500" :
                        selectedUser.user.membership?.tier === "pro" ? "bg-amber-500 text-black" :
                        "bg-stone-700"
                      }>
                        {selectedUser.user.membership?.tier || "free"}
                      </Badge>
                      {selectedUser.user.membership?.complimentary && (
                        <Badge className="bg-green-500">Complimentary</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4 text-center">
                      <Calendar className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{selectedUser.stats?.total_bookings || 0}</p>
                      <p className="text-xs text-gray-400">Total Bookings</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4 text-center">
                      <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{selectedUser.stats?.completed_bookings || 0}</p>
                      <p className="text-xs text-gray-400">Completed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4 text-center">
                      <Users className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{selectedUser.stats?.referrals_made || 0}</p>
                      <p className="text-xs text-gray-400">Referrals</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Subscription Info */}
                <Card className="bg-stone-800 border-stone-700">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-amber-400" />
                      Subscription Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">Tier</p>
                        <p className="text-white font-medium capitalize">{selectedUser.user.membership?.tier || "Free"}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Status</p>
                        <p className="text-white font-medium capitalize">{selectedUser.user.membership?.status || "None"}</p>
                      </div>
                      {selectedUser.user.membership?.end_date && (
                        <div>
                          <p className="text-gray-400">Expires</p>
                          <p className="text-white font-medium">{selectedUser.user.membership.end_date.split("T")[0]}</p>
                        </div>
                      )}
                      {selectedUser.user.membership?.granted_by && (
                        <div>
                          <p className="text-gray-400">Granted By</p>
                          <p className="text-white font-medium">{selectedUser.user.membership.granted_by}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Account Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Phone</p>
                    <p className="text-white">{selectedUser.user.phone || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Joined</p>
                    <p className="text-white">{selectedUser.user.created_at?.split("T")[0] || "Unknown"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">User ID</p>
                    <p className="text-white font-mono text-xs">{selectedUser.user.user_id || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Status</p>
                    <p className={`font-medium ${selectedUser.user.status === "active" ? "text-green-400" : "text-red-400"}`}>
                      {selectedUser.user.status || "Active"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Soldier Status</p>
                    <p className={`font-medium ${
                      selectedUser.user.soldier_status === 'active' ? "text-green-400" : 
                      selectedUser.user.is_soldier ? "text-amber-400" : "text-gray-500"
                    }`}>
                      {selectedUser.user.soldier_status === 'active' ? "Active Soldier" :
                       selectedUser.user.soldier_status === 'super_soldier' ? "Super Soldier ⭐" :
                       selectedUser.user.is_soldier ? "Pending Certification" : "Not a Soldier"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-400">Loading user details...</p>
              </div>
            )}
            <DialogFooter className="flex flex-wrap gap-2">
              {isSuperAdmin && selectedUser?.user && (
                <>
                  <Button 
                    onClick={() => setShowSubscriptionModal(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                    data-testid="manage-subscription-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Manage Subscription
                  </Button>
                  {/* Soldier Management Buttons */}
                  {!selectedUser.user.is_soldier && (
                    <Button 
                      onClick={() => handleMakeSoldier(selectedUser.user.user_id, selectedUser.user.email, selectedUser.user.name)}
                      className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black"
                      data-testid="make-soldier-modal-btn"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Make Soldier
                    </Button>
                  )}
                  {selectedUser.user.is_soldier && selectedUser.user.soldier_status !== 'super_soldier' && (
                    <Button 
                      onClick={() => handleMakeSuperSoldier(selectedUser.user.user_id, selectedUser.user.email, selectedUser.user.name)}
                      className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white"
                      data-testid="make-super-soldier-modal-btn"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Promote to Super Soldier
                    </Button>
                  )}
                  {selectedUser?.user?.membership?.tier !== "free" && (
                    <Button 
                      variant="outline"
                      onClick={() => handleCancelSubscription(selectedUser?.user?.user_id)}
                      className="border-red-500 text-red-400 hover:bg-red-500/20"
                    >
                      Cancel Subscription
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    onClick={() => handleDeleteUser(selectedUser?.user?.user_id, selectedUser?.user?.email)}
                    className="border-red-500 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User
                  </Button>
                </>
              )}
              <Button variant="ghost" onClick={() => setShowUserDetails(false)} className="text-gray-400">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Subscription Management Dialog */}
        <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Manage Subscription
              </DialogTitle>
            </DialogHeader>
            {selectedUser?.user && (
              <div className="space-y-4 py-4">
                <p className="text-gray-400">
                  Managing subscription for <span className="text-white font-medium">{selectedUser.user.name || "User"}</span>
                </p>
                
                <div>
                  <Label className="text-gray-300">Subscription Tier</Label>
                  <select
                    value={subscriptionForm.tier}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, tier: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-md text-white"
                    data-testid="subscription-tier-select"
                  >
                    <option value="pro">Pro ($29.99/mo)</option>
                    <option value="elite">Elite ($49.99/mo)</option>
                  </select>
                </div>

                <div>
                  <Label className="text-gray-300">Duration</Label>
                  <select
                    value={subscriptionForm.duration_months}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, duration_months: parseInt(e.target.value) })}
                    className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-md text-white"
                  >
                    <option value={1}>1 Month</option>
                    <option value={3}>3 Months</option>
                    <option value={6}>6 Months</option>
                    <option value={12}>12 Months</option>
                    <option value={24}>24 Months (Lifetime Beta)</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="complimentary"
                    checked={subscriptionForm.complimentary}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, complimentary: e.target.checked })}
                    className="rounded border-stone-700 bg-stone-800"
                  />
                  <Label htmlFor="complimentary" className="text-gray-300 cursor-pointer">
                    Complimentary (Free gift - no charge)
                  </Label>
                </div>

                {subscriptionForm.tier !== "free" && (
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardContent className="p-4">
                      <p className="text-amber-400 text-sm">
                        <Sparkles className="w-4 h-4 inline mr-2" />
                        This will grant <strong>{subscriptionForm.tier.toUpperCase()}</strong> access 
                        for <strong>{subscriptionForm.duration_months} month(s)</strong>
                        {subscriptionForm.complimentary && " as a complimentary gift"}.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowSubscriptionModal(false)} className="text-gray-400">
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateSubscription} 
                className="bg-amber-500 hover:bg-amber-600 text-black"
                data-testid="apply-subscription-btn"
              >
                Apply Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Notifications Button - Fixed Position */}
        {isSuperAdmin && (
          <Button
            onClick={handleTestNotifications}
            className="fixed bottom-6 right-24 bg-green-500 hover:bg-green-600 text-white shadow-lg"
            data-testid="test-notifications-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            Test SMS/Email
          </Button>
        )}

        {/* Floating User Guide Button */}
        <Link
          to="/user-guide?role=owner"
          className="fixed bottom-24 right-6 bg-violet-600 hover:bg-violet-700 text-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center z-40 transition-all hover:scale-110"
          title="Salon Owner Guide"
          data-testid="floating-guide-btn"
        >
          <BookOpen className="w-5 h-5" />
        </Link>

        {/* AI Chat Button */}
        <button
          onClick={() => setChatOpen(!chatOpen)}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg rounded-full w-14 h-14 flex items-center justify-center z-40 hover:scale-110 transition-all"
          data-testid="ai-chat-fab"
        >
          <MessageSquare className="w-6 h-6" />
        </button>

        {/* AI Chatbot */}
        {chatOpen && <AIChatbot onClose={() => setChatOpen(false)} userRole="owner" />}

        {/* Soldier Recruitment Playbook */}
        <SoldierRecruitmentPlaybook 
          isOpen={showPlaybook}
          onClose={() => setShowPlaybook(false)}
          referralCode={user?.soldier_referral_code || 'ADMIN-HQ'}
          discountValue="14 days FREE + $10 off for 3 months"
        />
      </main>
    </div>
  );
};

export default AdminDashboard;
